import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import BrokerMenu from '../components/BrokerMenu';
import { 
  BookOpen,
  TrendingUp, 
  TrendingDown, 
  MoreVertical,
  Trash2,
  CheckCircle,
  XCircle,
  Loader2,
  BarChart3,
  Clock,
  Calendar,
  Zap,
  Play,
  DollarSign,
  Trophy,
  AlertTriangle,
  Target,
  FileText,
  RefreshCw,
  Wallet,
  Hourglass,
  Archive,
  ChevronDown,
  ChevronUp,
  Activity,
  Filter,
  PieChart,
  Percent,
  Award,
  Flame,
  HelpCircle,
  Info,
  ArrowRightLeft,
  ArrowDownUp,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import InfoGuidePanel, { 
  MarketHoursDisplay, 
  Intraday24hWarning,
  IntradayInfoPopup,
  SwingInfoPopup,
  useInfoPopupPreferences,
  getMarketStatus
} from '../components/InfoGuide';
import { 
  ActiveFiltersBadge, 
  PortfolioSummaryCard,
  SubFilterTabs,
  AnalyticsFiltersPanel,
  PnlSummaryCard,
  ActivateTradeDialog,
  ResetPortfolioDialog,
  PnLHistoryDialog
} from '../components/history';
import axios from 'axios';
import { getAssetName } from '../utils/assetNames';
import { getSymbolName } from '../utils/symbolNames';
import OnboardingTutorial from '../components/OnboardingTutorial';
import { HISTORY_SLIDES } from '../config/tutorialSlides';
import HowItWorks from '../components/HowItWorks';
import EstimatedFeesField from '../components/EstimatedFeesField';
import GuestActionBanner from '../components/GuestActionBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fonction pour obtenir le nom lisible d'un symbole
const getDisplayName = (symbol, lang = 'fr') => {
  if (!symbol) return '';
  
  // Utiliser le nouveau module centralisé symbolNames.js
  const symbolName = getSymbolName(symbol);
  if (symbolName && symbolName !== symbol) {
    return symbolName;
  }
  
  // Essayer le mapping externe legacy (utils/assetNames.js)
  const externalName = getAssetName(symbol);
  if (externalName && externalName !== symbol) {
    return externalName;
  }
  
  // Pour les cryptos sans mapping, extraire le nom de base
  if (symbol.includes('/USDT')) {
    return symbol.replace('/USDT', '');
  }
  if (symbol.includes('USDT')) {
    return symbol.replace('USDT', '');
  }
  
  // Extraire le ticker si c'est au format EXCHANGE:TICKER
  if (symbol.includes(':')) {
    return symbol.split(':')[1];
  }
  
  // Retourner le symbole tel quel si pas de mapping
  return symbol;
};

// Helper function to get price with case-insensitive fallback
const getPriceForSymbol = (pricesObject, symbol) => {
  if (!symbol || !pricesObject) return undefined;
  
  // Try exact match first
  if (pricesObject[symbol] !== undefined) {
    return pricesObject[symbol];
  }
  
  // Fallback: case-insensitive match
  const matchingKey = Object.keys(pricesObject).find(
    k => k.toLowerCase() === symbol.toLowerCase()
  );
  
  if (matchingKey) {
    console.log(`[Price Lookup] Case-insensitive match: ${symbol} -> ${matchingKey}`);
    return pricesObject[matchingKey];
  }
  
  return undefined;
};


// Fonction pour formater les prix avec une précision adaptée
// Pour les petits prix (< 0.01), on affiche plus de décimales
const formatPrice = (price, forceDecimals = null) => {
  if (price === null || price === undefined || isNaN(price)) return '—';
  
  const absPrice = Math.abs(price);
  
  // Déterminer le nombre de décimales nécessaires
  let decimals;
  if (forceDecimals !== null) {
    decimals = forceDecimals;
  } else if (absPrice === 0) {
    decimals = 2;
  } else if (absPrice < 0.0001) {
    decimals = 8; // Très petits prix (comme certaines cryptos)
  } else if (absPrice < 0.001) {
    decimals = 6; // Petits prix
  } else if (absPrice < 0.01) {
    decimals = 5; // Prix comme VET, DOGE
  } else if (absPrice < 0.1) {
    decimals = 4;
  } else if (absPrice < 1) {
    decimals = 4;
  } else if (absPrice < 10) {
    decimals = 3;
  } else if (absPrice < 1000) {
    decimals = 2;
  } else {
    decimals = 2;
  }
  
  return price.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// Fonction pour déterminer la catégorie d'un actif
const getAssetCategory = (symbol) => {
  if (!symbol) return 'unknown';
  
  // Crypto
  if (symbol.includes('/USDT') || symbol.includes('-USD') || symbol.endsWith('USDT')) {
    return 'crypto';
  }
  
  // Commodités (futures Yahoo Finance)
  const commodities = ['CL=F', 'GC=F', 'SI=F', 'HG=F', 'NG=F', 'ZC=F', 'ZW=F', 'ZS=F', 'PL=F', 'PA=F'];
  if (commodities.includes(symbol) || symbol.endsWith('=F')) {
    return 'commodities';
  }
  
  // Indices
  const indices = ['^GSPC', '^DJI', '^IXIC', '^VIX', '^RUT', 'ES=F', 'NQ=F', 'YM=F'];
  if (indices.includes(symbol) || symbol.startsWith('^')) {
    return 'indices';
  }
  
  // ETF
  const etfs = ['SPY', 'QQQ', 'IWM', 'DIA', 'VTI', 'VOO', 'ARKK', 'XLF', 'XLE', 'GLD', 'SLV'];
  if (etfs.includes(symbol)) {
    return 'etf';
  }
  
  // Par défaut, c'est une action
  return 'stocks';
};

// Labels pour les catégories
const CATEGORY_LABELS = {
  crypto: { fr: 'Crypto', en: 'Crypto' },
  commodities: { fr: 'Matières Premières', en: 'Commodities' },
  stocks: { fr: 'Actions', en: 'Stocks' },
  etf: { fr: 'ETF', en: 'ETF' },
  indices: { fr: 'Indices', en: 'Indices' },
};

const TRADE_TYPE_LABELS = {
  swing: { fr: 'Swing', en: 'Swing' },
  intraday: { fr: 'Intraday', en: 'Intraday' },
};

const RISK_PROFILE_LABELS = {
  prudent: { fr: 'Prudent', en: 'Prudent' },
  balanced: { fr: 'Équilibré', en: 'Balanced' },
  dynamic: { fr: 'Dynamique', en: 'Dynamic' },
};

const ENTRY_TYPE_LABELS = {
  optimal: { fr: 'Entrée Optimale', en: 'Optimal Entry' },
  market: { fr: 'Au Marché', en: 'Market Entry' },
};

const LOSS_REASON_LABELS = {
  sl: { fr: 'Stop Loss', en: 'Stop Loss' },
  liquidation: { fr: 'Liquidation', en: 'Liquidation' },
};

const WIN_REASON_LABELS = {
  tp1: { fr: 'TP1 (25%)', en: 'TP1 (25%)' },
  tp2: { fr: 'TP2 (50%)', en: 'TP2 (50%)' },
  tp100: { fr: 'TP100 (100%)', en: 'TP100 (100%)' },
};

const DIRECTION_LABELS = {
  long: { fr: 'Long', en: 'Long' },
  short: { fr: 'Short', en: 'Short' },
};

const STATUS_LABELS = {
  win: { fr: 'Gagné', en: 'Won' },
  loss: { fr: 'Perdu', en: 'Lost' },
};

const History = () => {
  const { language } = useLanguage();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedTradeRef = useRef(null);
  const [highlightedTradeId, setHighlightedTradeId] = useState(null);
  const [trades, setTrades] = useState([]);
  const [paperTrades, setPaperTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subFilter, setSubFilter] = useState('pending'); // pending, active, closed (removed 'all')
  const [closedSortOrder, setClosedSortOrder] = useState('desc'); // 'desc' = récent→ancien, 'asc' = ancien→récent
  const [closingTrade, setClosingTrade] = useState(null);
  const [exitPrice, setExitPrice] = useState('');
  const [exitLevel, setExitLevel] = useState('tp100'); // tp1, tp2, tp100
  const [activeTab, setActiveTab] = useState('real'); // real or paper
  const [paperPortfolio, setPaperPortfolio] = useState(null);
  const [checkingPrices, setCheckingPrices] = useState(false);
  const [currentPrices, setCurrentPrices] = useState({}); // Prix actuels des actifs
  const [expandedTrades, setExpandedTrades] = useState({}); // Trades avec détails visibles
  const [showResetConfirm, setShowResetConfirm] = useState(false); // Modal de confirmation reset
  const [resetConfirmText, setResetConfirmText] = useState(''); // Texte de confirmation
  const [activatingTrade, setActivatingTrade] = useState(null); // Trade en cours d'activation
  const [activationPrice, setActivationPrice] = useState(''); // Prix d'entrée pour l'activation
  const [pnlHistoryDialog, setPnlHistoryDialog] = useState(null); // 'gains', 'losses', 'total', 'unrealized'
  const [isResetting, setIsResetting] = useState(false); // Loading state for reset
  
  // Handle trade_id from notification URL
  useEffect(() => {
    const tradeId = searchParams.get('trade_id');
    if (tradeId) {
      setHighlightedTradeId(tradeId);
      // Clear the URL param after reading
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Scroll to highlighted trade when loaded
  useEffect(() => {
    if (highlightedTradeId && !loading) {
      // Find in which tab/filter the trade is
      const allTrades = [...trades, ...paperTrades];
      const targetTrade = allTrades.find(t => t.id === highlightedTradeId);
      
      if (targetTrade) {
        // Switch to correct tab and filter
        if (targetTrade.is_paper) {
          setActiveTab('paper');
        } else {
          setActiveTab('real');
        }
        
        // Set correct sub-filter based on trade status
        if (targetTrade.status === 'pending') {
          setSubFilter('pending');
        } else if (targetTrade.status === 'active') {
          setSubFilter('active');
        } else {
          setSubFilter('closed');
        }
        
        // Expand the trade to show details
        setExpandedTrades(prev => ({ ...prev, [highlightedTradeId]: true }));
        
        // Scroll to trade after a short delay
        setTimeout(() => {
          if (highlightedTradeRef.current) {
            highlightedTradeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
        
        // Clear highlight after 5 seconds
        setTimeout(() => {
          setHighlightedTradeId(null);
        }, 5000);
      }
    }
  }, [highlightedTradeId, loading, trades, paperTrades]);
  
  // === PAGINATION TRADES TERMINÉS ===
  const [visibleClosedCount, setVisibleClosedCount] = useState(10); // Nombre de trades terminés visibles
  const [visiblePaperClosedCount, setVisiblePaperClosedCount] = useState(10); // Nombre de trades paper terminés visibles
  const TRADES_PER_PAGE = 10; // Nombre de trades à charger par clic
  
  // === FILTRES D'ANALYSE STATISTIQUE ===
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsFilters, setAnalyticsFilters] = useState({
    categories: [], // crypto, commodities, stocks, etf, indices
    tradeTypes: [], // swing, intraday
    riskProfiles: [], // prudent, balanced, dynamic
    entryTypes: [], // optimal, market
    tradeSources: [], // manual, autoscan
    lossReasons: [], // sl, liquidation
    winReasons: [], // tp1, tp2, tp100
    directions: [], // long, short
    statuses: [], // win, loss
  });
  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null,
  });

  // Toggle un filtre dans analyticsFilters
  const toggleAnalyticsFilter = (filterType, value) => {
    setAnalyticsFilters(prev => {
      const currentValues = prev[filterType] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [filterType]: newValues };
    });
  };

  // Réinitialiser tous les filtres
  const resetAnalyticsFilters = () => {
    setAnalyticsFilters({
      categories: [],
      tradeTypes: [],
      riskProfiles: [],
      entryTypes: [],
      tradeSources: [],
      lossReasons: [],
      winReasons: [],
      directions: [],
      statuses: [],
    });
    setDateFilter({ startDate: null, endDate: null });
    // Reset pagination when filters change
    setVisibleClosedCount(10);
    setVisiblePaperClosedCount(10);
  };
  
  // Handle sub-filter change with pagination reset
  const handleSubFilterChange = (newFilter) => {
    setSubFilter(newFilter);
    // Reset pagination when changing sub-filter
    setVisibleClosedCount(10);
    setVisiblePaperClosedCount(10);
  };

  // Déterminer les propriétés d'un trade pour le filtrage
  const getTradeProperties = (trade) => {
    const symbol = trade.symbol || '';
    const category = getAssetCategory(symbol);
    
    // Type de trade (swing/intraday) - basé sur auto_test_timeframe ou timeframe
    const tradeType = trade.auto_test_timeframe || trade.timeframe || 'swing';
    
    // Profil de risque
    const riskProfile = trade.auto_test_profile || trade.risk_profile || 
      (trade.leverage <= 10 ? 'prudent' : trade.leverage <= 20 ? 'balanced' : 'dynamic');
    
    // Type d'entrée - Amélioration de la détection
    // Priority: 1) auto_test_entry_type, 2) entry_type field, 3) status-based detection
    let entryType = trade.auto_test_entry_type || trade.entry_type;
    if (!entryType) {
      // Si le trade a été créé avec status 'active' directement, c'est une entrée au marché
      // Si créé avec status 'pending', c'est une entrée optimale
      if (trade.created_at_price !== null && trade.created_at_price !== undefined) {
        entryType = trade.entry_price === trade.created_at_price ? 'market' : 'optimal';
      } else {
        // Fallback: vérifier si le trade était pending (optimal) ou directement active (market)
        // Les trades "market" sont créés directement en 'active'
        // Les trades "optimal" sont créés en 'pending' puis passent en 'active'
        entryType = trade.original_status === 'pending' ? 'optimal' : 'market';
      }
    }
    
    // Direction du trade
    const direction = (trade.direction || trade.trade_direction || 'long').toLowerCase();
    
    // Raison de perte (pour les LOSS)
    // On calcule basé sur le prix de sortie vs SL vs Liquidation
    let lossReason = null;
    if (trade.status === 'LOSS') {
      // D'abord, vérifier si close_reason est défini et valide
      const closeReason = (trade.close_reason || '').toLowerCase();
      if (closeReason && closeReason !== 'none' && closeReason !== 'null') {
        lossReason = closeReason.includes('liquidat') ? 'liquidation' : 'sl';
      } else {
        // Calculer la raison basée sur les prix
        const entry = trade.entry_price || 0;
        const exitPrice = trade.exit_price || 0;
        const sl = trade.stop_loss || 0;
        const leverage = trade.leverage || 1;
        const isLong = direction === 'long';
        
        // Calculer le prix de liquidation (perte de 100% de la marge)
        // Pour LONG: liq = entry * (1 - 1/leverage)
        // Pour SHORT: liq = entry * (1 + 1/leverage)
        let liqPrice = 0;
        if (leverage > 1) {
          liqPrice = isLong 
            ? entry * (1 - 1/leverage)
            : entry * (1 + 1/leverage);
        }
        
        // Déterminer si c'est une liquidation ou un SL
        // LIQUIDATION = le prix a atteint le niveau de liquidation ET perte ~100% de la marge
        // SL = tous les autres cas de perte
        const margin = trade.margin || trade.position_size || 0;
        const pnl = trade.pnl || 0;
        const pnlPercent = margin > 0 ? Math.abs(pnl / margin) * 100 : 0;
        const isNearTotalLoss = pnlPercent >= 90; // Perte de 90%+ de la marge = probable liquidation
        
        const tolerance = entry * 0.01; // 1% de tolérance
        
        if (isLong) {
          // Pour LONG: liquidation si exit <= liq price ET perte proche de 100%
          const hitLiquidationPrice = liqPrice > 0 && exitPrice <= liqPrice + tolerance;
          
          if (hitLiquidationPrice && isNearTotalLoss) {
            lossReason = 'liquidation';
          } else {
            lossReason = 'sl';
          }
        } else {
          // Pour SHORT: liquidation si exit >= liq price ET perte proche de 100%
          const hitLiquidationPrice = liqPrice > 0 && exitPrice >= liqPrice - tolerance;
          
          if (hitLiquidationPrice && isNearTotalLoss) {
            lossReason = 'liquidation';
          } else {
            lossReason = 'sl';
          }
        }
      }
    }
    
    // Source du trade (manuel ou auto-scan)
    // IMPORTANT: Only auto_scan_created identifies trades from the Auto-Scan feature
    // auto_test is used for other automated trades (AI analysis, etc.) and should NOT be counted as Auto-Scan
    const tradeSource = trade.auto_scan_created ? 'autoscan' : 'manual';
    
    // Raison de gain (pour les WIN)
    let winReason = null;
    if (trade.status === 'WIN') {
      // D'abord, vérifier si exit_level est défini manuellement
      if (trade.exit_level) {
        winReason = trade.exit_level; // tp1, tp2, tp100
      } else {
        // Sinon, calculer basé sur le prix de sortie
        const exitPrice = trade.exit_price || trade.current_price;
        const tp1 = trade.take_profit_1;
        const tp2 = trade.take_profit_2;
        const tp100 = trade.take_profit_100;
        const direction = (trade.direction || '').toLowerCase();
        const isLong = direction === 'long';
        
        if (exitPrice) {
          // Calculer quelle cible a été atteinte en comparant le prix de sortie aux TPs
          // On utilise une tolérance de 1% pour les comparaisons
          let reachedTP100 = false, reachedTP2 = false, reachedTP1 = false;
          
          if (isLong) {
            if (tp100) reachedTP100 = exitPrice >= tp100 * 0.99;
            if (tp2) reachedTP2 = exitPrice >= tp2 * 0.99;
            if (tp1) reachedTP1 = exitPrice >= tp1 * 0.99;
          } else {
            if (tp100) reachedTP100 = exitPrice <= tp100 * 1.01;
            if (tp2) reachedTP2 = exitPrice <= tp2 * 1.01;
            if (tp1) reachedTP1 = exitPrice <= tp1 * 1.01;
          }
          
          // Priorité: TP100 > TP2 > TP1
          if (reachedTP100) {
            winReason = 'tp100';
          } else if (reachedTP2) {
            winReason = 'tp2';
          } else if (reachedTP1) {
            winReason = 'tp1';
          } else {
            // Si aucun TP n'est défini ou atteint, essayer de deviner basé sur le close_reason
            const closeReason = (trade.close_reason || '').toLowerCase();
            if (closeReason.includes('tp100') || closeReason.includes('tp 100')) {
              winReason = 'tp100';
            } else if (closeReason.includes('tp2') || closeReason.includes('tp 2')) {
              winReason = 'tp2';
            } else if (closeReason.includes('tp1') || closeReason.includes('tp 1')) {
              winReason = 'tp1';
            } else {
              // Par défaut basé sur le gain relatif
              const entry = trade.entry_price || 0;
              if (entry > 0) {
                const gainPct = isLong 
                  ? ((exitPrice - entry) / entry) * 100
                  : ((entry - exitPrice) / entry) * 100;
                // Estimation: <5% = TP1, 5-15% = TP2, >15% = TP100
                if (gainPct >= 15) winReason = 'tp100';
                else if (gainPct >= 5) winReason = 'tp2';
                else winReason = 'tp1';
              } else {
                winReason = 'tp1'; // Fallback final
              }
            }
          }
        } else {
          winReason = 'tp1'; // Si pas de prix de sortie, default à TP1
        }
      }
    }
    
    return { category, tradeType, riskProfile, entryType, direction, lossReason, winReason, tradeSource };
  };

  // Filtrer les trades terminés selon les filtres d'analyse ET la date
  const getFilteredClosedTrades = (tradesList) => {
    const closedTrades = tradesList.filter(t => t.status === 'WIN' || t.status === 'LOSS');
    
    // Si aucun filtre actif, retourner tous les trades terminés
    const hasActiveFilters = Object.values(analyticsFilters).some(arr => arr.length > 0);
    const hasDateFilter = dateFilter.startDate || dateFilter.endDate;
    
    if (!hasActiveFilters && !hasDateFilter) return closedTrades;
    
    return closedTrades.filter(trade => {
      const props = getTradeProperties(trade);
      
      // Filtre par date (dateFilter contient maintenant des objets Date)
      if (hasDateFilter) {
        const tradeDate = new Date(trade.closed_at || trade.created_at);
        if (dateFilter.startDate) {
          const startDate = new Date(dateFilter.startDate);
          startDate.setHours(0, 0, 0, 0);
          if (tradeDate < startDate) return false;
        }
        if (dateFilter.endDate) {
          const endDate = new Date(dateFilter.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (tradeDate > endDate) return false;
        }
      }
      
      // Vérifier chaque filtre (OR dans la même catégorie, AND entre catégories)
      if (analyticsFilters.categories.length > 0 && !analyticsFilters.categories.includes(props.category)) {
        return false;
      }
      if (analyticsFilters.tradeTypes.length > 0 && !analyticsFilters.tradeTypes.includes(props.tradeType)) {
        return false;
      }
      if (analyticsFilters.riskProfiles.length > 0 && !analyticsFilters.riskProfiles.includes(props.riskProfile)) {
        return false;
      }
      if (analyticsFilters.entryTypes.length > 0 && !analyticsFilters.entryTypes.includes(props.entryType)) {
        return false;
      }
      if (analyticsFilters.tradeSources.length > 0 && !analyticsFilters.tradeSources.includes(props.tradeSource)) {
        return false;
      }
      if (analyticsFilters.directions.length > 0 && !analyticsFilters.directions.includes(props.direction)) {
        return false;
      }
      
      // Filtre par statut (WIN/LOSS)
      if (analyticsFilters.statuses.length > 0) {
        const tradeStatus = trade.status.toLowerCase();
        if (!analyticsFilters.statuses.includes(tradeStatus)) {
          return false;
        }
      }
      
      // Filtre par raison de perte - SI sélectionné, n'afficher QUE les trades LOSS avec cette raison
      if (analyticsFilters.lossReasons.length > 0) {
        // Si on filtre par raison de perte, on exclut automatiquement les WIN
        if (trade.status !== 'LOSS') return false;
        if (!analyticsFilters.lossReasons.includes(props.lossReason)) return false;
      }
      
      // Filtre par niveau de sortie WIN - SI sélectionné, n'afficher QUE les trades WIN avec ce niveau
      if (analyticsFilters.winReasons.length > 0) {
        // Si on filtre par niveau de sortie WIN, on exclut automatiquement les LOSS
        if (trade.status !== 'WIN') return false;
        if (!analyticsFilters.winReasons.includes(props.winReason)) return false;
      }
      
      return true;
    });
  };
  
  // Vérifier si les filtres d'analyse sont actifs
  const hasActiveAnalyticsFilters = () => {
    return Object.values(analyticsFilters).some(arr => arr.length > 0) || 
           dateFilter.startDate || dateFilter.endDate;
  };

  // Handler pour supprimer un filtre individuel
  const handleRemoveFilter = (filter) => {
    if (filter.type === 'date') {
      setDateFilter({ startDate: null, endDate: null });
    } else {
      const filterTypeMap = {
        category: 'categories',
        tradeType: 'tradeTypes',
        riskProfile: 'riskProfiles',
        entryType: 'entryTypes',
        direction: 'directions',
        status: 'statuses',
        lossReason: 'lossReasons',
        winReason: 'winReasons',
      };
      const filterKey = filterTypeMap[filter.type];
      if (filterKey) {
        toggleAnalyticsFilter(filterKey, filter.value);
      }
    }
  };

  // Obtenir les labels des filtres actifs pour l'affichage
  const getActiveFilterLabels = () => {
    const labels = [];
    
    // Categories
    analyticsFilters.categories.forEach(cat => {
      const label = CATEGORY_LABELS[cat]?.[language] || cat;
      labels.push({ type: 'category', value: cat, label, color: 'blue' });
    });
    
    // Trade Types
    analyticsFilters.tradeTypes.forEach(type => {
      const label = TRADE_TYPE_LABELS[type]?.[language] || type;
      labels.push({ type: 'tradeType', value: type, label, color: 'cyan' });
    });
    
    // Risk Profiles
    analyticsFilters.riskProfiles.forEach(profile => {
      const label = RISK_PROFILE_LABELS[profile]?.[language] || profile;
      const color = profile === 'prudent' ? 'green' : profile === 'balanced' ? 'yellow' : 'red';
      labels.push({ type: 'riskProfile', value: profile, label, color });
    });
    
    // Entry Types
    analyticsFilters.entryTypes.forEach(entry => {
      const label = ENTRY_TYPE_LABELS[entry]?.[language] || entry;
      labels.push({ type: 'entryType', value: entry, label, color: 'indigo' });
    });
    
    // Directions
    analyticsFilters.directions.forEach(dir => {
      const label = DIRECTION_LABELS[dir]?.[language] || dir;
      const color = dir === 'long' ? 'green' : 'red';
      labels.push({ type: 'direction', value: dir, label, color });
    });
    
    // Statuses
    analyticsFilters.statuses.forEach(status => {
      const label = STATUS_LABELS[status]?.[language] || status;
      const color = status === 'win' ? 'green' : 'red';
      labels.push({ type: 'status', value: status, label, color });
    });
    
    // Loss Reasons
    analyticsFilters.lossReasons.forEach(reason => {
      const label = LOSS_REASON_LABELS[reason]?.[language] || reason;
      labels.push({ type: 'lossReason', value: reason, label, color: 'red' });
    });
    
    // Win Reasons
    analyticsFilters.winReasons.forEach(reason => {
      const label = WIN_REASON_LABELS[reason]?.[language] || reason;
      labels.push({ type: 'winReason', value: reason, label, color: 'green' });
    });
    
    // Date filter
    if (dateFilter.startDate || dateFilter.endDate) {
      let dateLabel = '';
      if (dateFilter.startDate && dateFilter.endDate) {
        dateLabel = `${format(dateFilter.startDate, 'dd/MM/yy')} → ${format(dateFilter.endDate, 'dd/MM/yy')}`;
      } else if (dateFilter.startDate) {
        dateLabel = language === 'fr' ? `Depuis ${format(dateFilter.startDate, 'dd/MM/yy')}` : `From ${format(dateFilter.startDate, 'dd/MM/yy')}`;
      } else if (dateFilter.endDate) {
        dateLabel = language === 'fr' ? `Jusqu'au ${format(dateFilter.endDate, 'dd/MM/yy')}` : `Until ${format(dateFilter.endDate, 'dd/MM/yy')}`;
      }
      labels.push({ type: 'date', value: 'date', label: dateLabel, color: 'purple' });
    }
    
    return labels;
  };

  // Helper function to calculate total gain for a WIN trade
  // Calculer les statistiques détaillées pour les trades filtrés
  const calculateDetailedStats = (filteredTrades) => {
    const wins = filteredTrades.filter(t => t.status === 'WIN');
    const losses = filteredTrades.filter(t => t.status === 'LOSS');
    
    // Use actual PnL values from trades
    // WIN trades have positive pnl, LOSS trades have negative pnl
    const totalGains = wins.reduce((sum, t) => sum + (t.pnl || 0), 0);
    const totalLosses = Math.abs(losses.reduce((sum, t) => sum + (t.pnl || 0), 0));
    const totalPnl = totalGains - totalLosses;
    
    const winRate = filteredTrades.length > 0 ? (wins.length / filteredTrades.length * 100) : 0;
    const avgWin = wins.length > 0 ? totalGains / wins.length : 0;
    const avgLoss = losses.length > 0 ? totalLosses / losses.length : 0;
    const profitFactor = totalLosses > 0 ? totalGains / totalLosses : totalGains > 0 ? Infinity : 0;
    
    // Stats par raison de clôture
    const byCloseReason = {};
    filteredTrades.forEach(t => {
      const reason = t.close_reason || 'Unknown';
      if (!byCloseReason[reason]) {
        byCloseReason[reason] = { count: 0, pnl: 0 };
      }
      byCloseReason[reason].count++;
      // Use actual PnL value from trade
      byCloseReason[reason].pnl += t.pnl || 0;
    });
    
    return {
      total: filteredTrades.length,
      wins: wins.length,
      losses: losses.length,
      totalPnl,
      totalGains,
      totalLosses,
      winRate,
      avgWin,
      avgLoss,
      profitFactor,
      byCloseReason,
    };
  };

  // Toggle expanded state for a trade
  const toggleExpanded = (tradeId) => {
    setExpandedTrades(prev => ({
      ...prev,
      [tradeId]: !prev[tradeId]
    }));
  };

  // Calculate totals for trades
  // Calculate totals based on trade status filter
  const calculateTotals = (tradesList, filterType = 'all') => {
    let closedGains = 0;      // Gains des trades clôturés gagnants
    let closedLosses = 0;     // Pertes des trades clôturés perdants
    let unrealizedPnl = 0;    // PnL latent sur trades actifs (hors TP partiels)
    let partialTpGains = 0;   // Gains partiels des TP touchés sur trades ACTIFS seulement
    
    tradesList.forEach(trade => {
      const warnings = trade.warnings_sent || {};
      
      // TRADES CLÔTURÉS (WIN ou LOSS)
      // Le champ trade.pnl contient DÉJÀ le gain/perte total (incluant les partiels)
      // Ne PAS ajouter les partiels séparément pour éviter le double comptage
      if (trade.status === 'WIN' || trade.status === 'LOSS') {
        const pnl = trade.pnl || 0;
        if (trade.status === 'WIN') {
          closedGains += pnl;
        } else {
          closedLosses += Math.abs(pnl);
        }
        // NE PAS ajouter tradePartialGains ici - déjà inclus dans pnl
      }
      
      // TRADES ACTIFS - Seuls les trades actifs ont des gains partiels "non réalisés"
      if (trade.status === 'active') {
        const tp1 = trade.take_profit_1;
        const tp2 = trade.take_profit_2;
        const entry = trade.entry_price;
        const marge = trade.position_size || 0;
        const leverage = trade.leverage || 1;
        const isLong = trade.direction === 'LONG';
        const currentPrice = getPriceForSymbol(currentPrices, trade.symbol);
        
        // Dynamically check if TPs should be hit based on current price
        let tp1ShouldBeHit = warnings.tp1_hit || false;
        let tp2ShouldBeHit = warnings.tp2_hit || false;
        
        if (currentPrice && entry && tp1) {
          if (isLong && currentPrice >= tp1) tp1ShouldBeHit = true;
          if (!isLong && currentPrice <= tp1) tp1ShouldBeHit = true;
        }
        if (currentPrice && entry && tp2) {
          if (isLong && currentPrice >= tp2) tp2ShouldBeHit = true;
          if (!isLong && currentPrice <= tp2) tp2ShouldBeHit = true;
        }
        
        // Gains partiels sécurisés (UNIQUEMENT pour trades actifs)
        if (tp1ShouldBeHit && tp1 && entry) {
          const tp1DistPct = Math.abs(tp1 - entry) / entry * 100;
          partialTpGains += (tp1DistPct / 100) * marge * leverage * 0.25;
        }
        if (tp2ShouldBeHit && tp2 && entry) {
          const tp2DistPct = Math.abs(tp2 - entry) / entry * 100;
          partialTpGains += (tp2DistPct / 100) * marge * leverage * 0.25;
        }
        
        // PnL latent sur la portion restante (après TPs partiels)
        if (currentPrice && entry) {
          let pnlPct;
          if (isLong) {
            pnlPct = (currentPrice - entry) / entry * 100 * leverage;
          } else {
            pnlPct = (entry - currentPrice) / entry * 100 * leverage;
          }
          
          // Portion TP1 (25%) - si pas touché, PnL plafonné à TP1 (si TP1 défini)
          let pnlTp1Portion = 0;
          if (!tp1ShouldBeHit) {
            const tp1MaxPct = tp1 && entry > 0 ? Math.abs(tp1 - entry) / entry * 100 * leverage : Infinity;
            const cappedPct = Math.min(pnlPct, tp1MaxPct);
            pnlTp1Portion = (cappedPct / 100) * marge * 0.25;
          }
          
          // Portion TP2 (25%) - si pas touché, PnL plafonné à TP2 (si TP2 défini)
          let pnlTp2Portion = 0;
          if (!tp2ShouldBeHit) {
            const tp2MaxPct = tp2 && entry > 0 ? Math.abs(tp2 - entry) / entry * 100 * leverage : Infinity;
            const cappedPct = Math.min(pnlPct, tp2MaxPct);
            pnlTp2Portion = (cappedPct / 100) * marge * 0.25;
          }
          
          // Portion TP100 (50%) - PnL complet (pas de plafond)
          const pnlTp100Portion = (pnlPct / 100) * marge * 0.50;
          
          // PnL latent = somme des portions non sécurisées
          // LIQUIDATION: une perte latente ne peut JAMAIS dépasser la marge investie
          unrealizedPnl += Math.max(pnlTp1Portion + pnlTp2Portion + pnlTp100Portion, -marge);
        }
      }
      
      // Note: Trades PENDING ne peuvent pas avoir de gains partiels TP
      // car ils ne sont pas encore activés - aucun calcul nécessaire
    });
    
    return {
      closedGains,           // Gains des trades clôturés
      closedLosses,          // Pertes des trades clôturés
      realizedPnl: closedGains - closedLosses, // PnL total réalisé (clôturés uniquement)
      unrealizedPnl,         // PnL latent (non sécurisé, peut changer)
      partialTpGains         // Gains partiels TP (sécurisés mais trade pas fermé)
    };
  };

  const fetchTrades = useCallback(async () => {
    try {
      // Fetch ALL real trades (including pending)
      const realResponse = await axios.get(`${API}/trades?is_paper=false`);
      setTrades(realResponse.data);
      
      // Fetch ALL paper trades (including pending)
      const paperResponse = await axios.get(`${API}/trades?is_paper=true`);
      setPaperTrades(paperResponse.data);
      
      // Fetch paper portfolio
      const portfolioResponse = await axios.get(`${API}/paper-trading/portfolio`);
      setPaperPortfolio(portfolioResponse.data);
    } catch (error) {
      console.error('Failed to fetch trades:', error);
      toast.error('Failed to load trades');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTrades();
    } else {
      setLoading(false);
    }
  }, [fetchTrades, isAuthenticated]);

  // Check prices and auto-activate/close trades
  const checkPrices = async (showToast = true) => {
    setCheckingPrices(true);
    try {
      const response = await axios.post(`${API}/trades/check-prices`);
      const { activated_trades, closed_trades, current_prices, symbols_checked, message } = response.data;
      
      // DEBUG: Log API response to understand why prices might be missing
      console.log('[checkPrices] Response:', {
        message,
        symbolsChecked: symbols_checked,
        pricesReceived: Object.keys(current_prices || {}),
        priceValues: current_prices,
        activatedCount: activated_trades?.length || 0,
        closedCount: closed_trades?.length || 0
      });
      
      // Store current prices
      if (current_prices && Object.keys(current_prices).length > 0) {
        setCurrentPrices(current_prices);
        console.log('[checkPrices] Stored prices for symbols:', Object.keys(current_prices));
      } else {
        console.warn('[checkPrices] No prices received from API');
      }
      
      // Show notifications for activated trades
      if (activated_trades?.length > 0) {
        activated_trades.forEach(trade => {
          toast.success(
            language === 'fr' 
              ? `Trade ${getDisplayName(trade.symbol, 'fr')} ACTIVE a ${trade.activated_price.toFixed(2)}$`
              : `Trade ${getDisplayName(trade.symbol, 'en')} ACTIVATED at $${trade.activated_price.toFixed(2)}`,
            { duration: 5000 }
          );
        });
      }
      
      // Show notifications for closed trades
      if (closed_trades?.length > 0) {
        closed_trades.forEach(trade => {
          const icon = trade.status === 'WIN' ? '' : '';
          const pnlColor = trade.pnl >= 0 ? '+' : '';
          toast[trade.status === 'WIN' ? 'success' : 'error'](
            language === 'fr' 
              ? `${icon} Trade ${getDisplayName(trade.symbol, 'fr')} cloture (${trade.close_reason}) : ${pnlColor}${trade.pnl.toFixed(2)}$`
              : `${icon} Trade ${getDisplayName(trade.symbol, 'en')} closed (${trade.close_reason}): ${pnlColor}$${trade.pnl.toFixed(2)}`,
            { duration: 5000 }
          );
        });
      }
      
      // Refresh trades if any changes
      if (activated_trades?.length > 0 || closed_trades?.length > 0) {
        fetchTrades();
      } else if (showToast) {
        toast.info(language === 'fr' ? 'Aucun changement de statut' : 'No status changes');
      }
    } catch (error) {
      console.error('Failed to check prices:', error);
      if (showToast) {
        toast.error(language === 'fr' ? 'Erreur de verification' : 'Failed to check prices');
      }
    } finally {
      setCheckingPrices(false);
    }
  };

  // Auto-check prices every 30 seconds when there are pending/active trades
  useEffect(() => {
    // Always load prices on mount
    checkPrices(false);
    
    const hasPendingOrActive = [...trades, ...paperTrades].some(
      t => t.status === 'pending' || t.status === 'active'
    );
    
    if (hasPendingOrActive) {
      // Then check every 30 seconds
      const interval = setInterval(() => checkPrices(false), 30000);
      
      return () => {
        clearInterval(interval);
      };
    }
    // Note: checkPrices is intentionally excluded to prevent infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades, paperTrades, language]);

  // Reset paper portfolio
  const resetPaperPortfolio = async () => {
    // Require typing "RESET" to confirm
    if (resetConfirmText !== 'RESET') {
      toast.error(language === 'fr' ? 'Tapez RESET pour confirmer' : 'Type RESET to confirm');
      return;
    }
    
    setIsResetting(true);
    try {
      await axios.post(`${API}/paper-trading/reset`);
      toast.success(language === 'fr' ? 'Portfolio réinitialisé' : 'Portfolio reset');
      setShowResetConfirm(false);
      setResetConfirmText('');
      fetchTrades();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de réinitialisation' : 'Failed to reset');
    } finally {
      setIsResetting(false);
    }
  };

  const updateTradeStatus = async (tradeId, status, exitPriceValue = null, exitLevelValue = null) => {
    try {
      const payload = { status };
      if (exitPriceValue) {
        payload.exit_price = parseFloat(exitPriceValue);
      }
      if (exitLevelValue && status === 'WIN') {
        payload.exit_level = exitLevelValue; // tp1, tp2, tp100
      }
      const response = await axios.patch(`${API}/trades/${tradeId}/status`, payload);
      
      // Update local state with PNL data - check both trades and paperTrades
      const updateTrade = (t) => {
        if (t.id === tradeId) {
          return { 
            ...t, 
            status,
            exit_price: exitPriceValue ? parseFloat(exitPriceValue) : t.exit_price,
            exit_level: exitLevelValue || t.exit_level,
            pnl: response.data.pnl,
            pnl_percentage: response.data.pnl_percentage,
            closed_at: new Date().toISOString()
          };
        }
        return t;
      };
      
      setTrades(trades.map(updateTrade));
      setPaperTrades(paperTrades.map(updateTrade));
      
      setClosingTrade(null);
      setExitPrice('');
      setExitLevel('tp100');
      toast.success(language === 'fr' ? 'Trade clôturé avec succès' : 'Trade closed successfully');
    } catch (error) {
      console.error('Failed to update trade:', error);
      toast.error('Failed to update trade');
    }
  };

  // Handle closing a trade at market price. Status (WIN/LOSS) is computed by backend based on real PnL.
  const handleCloseTrade = (trade, status) => {
    if (status === 'active') {
      // Ouvrir le dialog d'activation avec le prix actuel du marché
      const currentPrice = getPriceForSymbol(currentPrices, trade.symbol);
      setActivatingTrade(trade);
      setActivationPrice(currentPrice?.toString() || trade.entry_price?.toString() || '');
    } else {
      // status is 'CLOSE' (or legacy 'WIN'/'LOSS' — treated the same, backend decides)
      setClosingTrade({ ...trade, targetStatus: 'CLOSE' });
      // Default exit = current market price, fallback to entry
      const currentPrice = getPriceForSymbol(currentPrices, trade.symbol);
      const defaultPrice = currentPrice || trade.entry_price;
      setExitPrice(defaultPrice?.toString() || '');
    }
  };

  // Confirm activation with new entry price
  const confirmActivateTrade = async () => {
    if (!activatingTrade || !activationPrice) return;
    
    try {
      const newEntryPrice = parseFloat(activationPrice);
      
      // Update trade with new entry price and set status to active
      const response = await axios.patch(`${API}/trades/${activatingTrade.id}/activate`, {
        entry_price: newEntryPrice
      });
      
      // Update local state
      const updateTrade = (t) => {
        if (t.id === activatingTrade.id) {
          return { 
            ...t, 
            status: 'active',
            entry_price: newEntryPrice,
            activated_at: new Date().toISOString()
          };
        }
        return t;
      };
      
      setTrades(trades.map(updateTrade));
      setPaperTrades(paperTrades.map(updateTrade));
      
      setActivatingTrade(null);
      setActivationPrice('');
      toast.success(language === 'fr' ? 'Trade activé avec succès' : 'Trade activated successfully');
    } catch (error) {
      console.error('Failed to activate trade:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'activation' : 'Failed to activate trade');
    }
  };

  const confirmCloseTrade = () => {
    if (closingTrade && exitPrice) {
      // Backend computes actual WIN/LOSS from real PnL — we send WIN by default so it goes
      // through the full close pipeline (partial exit + market close).
      updateTradeStatus(closingTrade.id, 'WIN', exitPrice, null);
    }
  };

  const deleteTrade = async (tradeId) => {
    try {
      await axios.delete(`${API}/trades/${tradeId}`);
      // Update both real and paper trades lists
      setTrades(trades.filter(t => t.id !== tradeId));
      setPaperTrades(paperTrades.filter(t => t.id !== tradeId));
      toast.success(language === 'fr' ? 'Trade supprimé' : 'Trade deleted');
    } catch (error) {
      console.error('Failed to delete trade:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'Failed to delete trade');
    }
  };

  // Convert Intraday trade to Swing
  const convertToSwing = async (trade) => {
    try {
      await axios.patch(`${API}/trades/${trade.id}`, {
        trade_type: 'SWING',
        auto_test_timeframe: 'swing'
      });
      
      // Update local state
      const updateTrade = (t) => {
        if (t.id === trade.id) {
          return { ...t, trade_type: 'SWING', auto_test_timeframe: 'swing' };
        }
        return t;
      };
      
      setTrades(trades.map(updateTrade));
      setPaperTrades(paperTrades.map(updateTrade));
      
      toast.success(language === 'fr' ? 'Trade converti en Swing' : 'Trade converted to Swing');
    } catch (error) {
      console.error('Failed to convert trade:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la conversion' : 'Failed to convert trade');
    }
  };

  // Check if trade is intraday and over 24h
  const isIntradayOver24h = (trade) => {
    if (trade.trade_type !== 'INTRADAY' || trade.status !== 'active') return false;
    const createdAt = new Date(trade.created_at);
    const now = new Date();
    const diffHours = (now - createdAt) / (1000 * 60 * 60);
    return diffHours >= 24;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'WIN':
        return 'bg-primary/20 text-primary';
      case 'LOSS':
        return 'bg-destructive/20 text-destructive';
      case 'active':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'pending':
        return 'bg-orange-500/20 text-orange-400';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'WIN':
        return language === 'fr' ? 'Gagné' : 'Win';
      case 'LOSS':
        return language === 'fr' ? 'Perdu' : 'Loss';
      case 'active':
        return language === 'fr' ? 'En cours' : 'Active';
      case 'pending':
        return language === 'fr' ? 'En attente' : 'Pending';
      default:
        return status;
    }
  };

  // Filter trades based on active tab and sub-filter
  const currentTrades = activeTab === 'real' ? trades : paperTrades;
  
  // Function to sort trades by date (most recent first)
  const sortTradesByDate = (tradesToSort, status, order = 'desc') => {
    return [...tradesToSort].sort((a, b) => {
      // For closed trades (WIN/LOSS), sort by closed_at
      if (status === 'closed' || a.status === 'WIN' || a.status === 'LOSS' || b.status === 'WIN' || b.status === 'LOSS') {
        const dateA = a.closed_at ? new Date(a.closed_at) : new Date(a.created_at);
        const dateB = b.closed_at ? new Date(b.closed_at) : new Date(b.created_at);
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }
      // For pending/active trades, sort by created_at (always most recent first)
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });
  };

  // Apply sub-filter AND analytics filters for closed trades, then sort by date
  const filteredTrades = subFilter === 'all'
    ? sortTradesByDate(currentTrades, 'all')
    : subFilter === 'pending'
      ? sortTradesByDate(currentTrades.filter(t => t.status === 'pending'), 'pending')
      : subFilter === 'active'
        ? sortTradesByDate(currentTrades.filter(t => t.status === 'active'), 'active')
        : sortTradesByDate(getFilteredClosedTrades(currentTrades), 'closed', closedSortOrder); // Apply analytics filters for closed trades

  // Separate filtered trades for Paper Trading tab (always use paperTrades), sorted by date
  const filteredPaperTrades = subFilter === 'all'
    ? sortTradesByDate(paperTrades, 'all')
    : subFilter === 'pending'
      ? sortTradesByDate(paperTrades.filter(t => t.status === 'pending'), 'pending')
      : subFilter === 'active'
        ? sortTradesByDate(paperTrades.filter(t => t.status === 'active'), 'active')
        : sortTradesByDate(getFilteredClosedTrades(paperTrades), 'closed', closedSortOrder); // Apply analytics filters for closed trades

  // Calculate comprehensive stats for real trades
  const pendingTrades = trades.filter(t => t.status === 'pending');
  const activeTrades = trades.filter(t => t.status === 'active');
  const closedTrades = trades.filter(t => t.status === 'WIN' || t.status === 'LOSS');
  
  // ========================================
  // SINGLE SOURCE OF TRUTH - Real Trades
  // ========================================
  // IMPORTANT: filteredClosedTrades is THE source for all filtered closed trade calculations
  const filteredClosedTrades = getFilteredClosedTrades(trades);
  const realTradesStats = calculateDetailedStats(filteredClosedTrades);
  const winningTrades = filteredClosedTrades.filter(t => t.status === 'WIN');
  const losingTrades = filteredClosedTrades.filter(t => t.status === 'LOSS');
  const totalGains = realTradesStats.totalGains;
  const totalLosses = realTradesStats.totalLosses;
  const totalPnl = realTradesStats.totalPnl;

  // ========================================
  // SINGLE SOURCE OF TRUTH - Paper Trades
  // ========================================
  // IMPORTANT: filteredPaperClosedTrades is THE source for all filtered closed trade calculations
  const filteredPaperClosedTrades = getFilteredClosedTrades(paperTrades);
  const paperTradesStats = calculateDetailedStats(filteredPaperClosedTrades);
  const paperPendingTrades = paperTrades.filter(t => t.status === 'pending');
  const paperActiveTrades = paperTrades.filter(t => t.status === 'active');
  const paperClosedTrades = paperTrades.filter(t => t.status === 'WIN' || t.status === 'LOSS');
  const paperWinningTrades = filteredPaperClosedTrades.filter(t => t.status === 'WIN');
  const paperLosingTrades = filteredPaperClosedTrades.filter(t => t.status === 'LOSS');
  // USE VALUES FROM paperTradesStats ONLY
  const paperTotalGains = paperTradesStats.totalGains;
  const paperTotalLosses = paperTradesStats.totalLosses;
  const paperTotalPnl = paperTradesStats.totalPnl;
  
  // Best and worst trades
  const bestTrade = closedTrades.length > 0 
    ? closedTrades.reduce((best, t) => (t.pnl || 0) > (best.pnl || 0) ? t : best, closedTrades[0])
    : null;
  const worstTrade = closedTrades.length > 0
    ? closedTrades.reduce((worst, t) => (t.pnl || 0) < (worst.pnl || 0) ? t : worst, closedTrades[0])
    : null;

  // Stats
  const stats = {
    total: trades.length,
    pending: pendingTrades.length,
    active: activeTrades.length,
    wins: winningTrades.length,
    losses: losingTrades.length,
  };
  const winRate = (stats.wins + stats.losses) > 0 
    ? ((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1) 
    : 0;

  // Profit Factor and average calculations for real trades - FROM SINGLE SOURCE
  const profitFactor = realTradesStats.profitFactor;
  const avgWin = realTradesStats.avgWin;
  const avgLoss = realTradesStats.avgLoss;

  // Paper trading Profit Factor and averages - FROM SINGLE SOURCE
  const paperProfitFactor = paperTradesStats.profitFactor;
  const paperAvgWin = paperTradesStats.avgWin;
  const paperAvgLoss = paperTradesStats.avgLoss;

  // Paper trading win rate - FROM SINGLE SOURCE
  const paperWinRate = paperTradesStats.winRate.toFixed(1);

  if (loading || authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // If not authenticated, we render the full interface with an inline guest
  // CTA banner (no blur) — same UX as Analyse IA / Calculateur.

  return (
    <div className="min-h-[calc(100vh-64px)] p-4 sm:p-6 lg:p-8">
      {/* Onboarding Tutorial */}
      <OnboardingTutorial
        pageId="history"
        slides={HISTORY_SLIDES}
        storageKey="tutorial_history_seen"
      />

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Guest CTA banner — full interface stays visible, actions are member-only */}
        <GuestActionBanner
          title="Le Journal enregistre chacun de tes trades"
          description="Aperçu invité. Créez un compte gratuit pour ajouter des trades, suivre vos performances et calculer votre PnL en direct."
        />

        {/* Market Hours Banner */}
        <MarketHoursDisplay />
        
        {/* Header with Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <BookOpen className="w-8 h-8 text-primary" />
                {language === 'fr' ? 'Journal de Trading' : 'Trading Journal'}
              </h1>
              <HowItWorks pageKey="history" />
            </div>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Gérez vos trades en attente, en cours et terminés' : 'Manage your pending, active and closed trades'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <EstimatedFeesField data-testid="journal-fees-field" />
            {/* Info Guide Button */}
            <InfoGuidePanel>
              <Button 
                variant="outline" 
                className="border-primary/30 text-primary hover:bg-primary/10"
                data-testid="journal-info-guide-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Guide' : 'Guide'}
              </Button>
            </InfoGuidePanel>
            
            <Button 
              onClick={checkPrices} 
              disabled={checkingPrices}
              variant="outline"
              className="border-primary/50"
            >
              {checkingPrices ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {language === 'fr' ? 'Vérifier les prix' : 'Check Prices'}
            </Button>

            <BrokerMenu label={language === 'fr' ? 'Ouvrir chez un broker' : 'Open at a broker'} />
          </div>
        </div>

        {/* Main Tabs: Real vs Paper */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="real" className="flex items-center gap-2" data-testid="tab-real-trades">
              <DollarSign className="w-4 h-4" />
              {language === 'fr' ? 'Trades Réels' : 'Real Trades'}
              <span className="ml-1 px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs">
                {trades.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="paper" className="flex items-center gap-2" data-testid="tab-paper-trades">
              <FileText className="w-4 h-4" />
              Paper Trading
              <span className="ml-1 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs">
                {paperTrades.length}
              </span>
            </TabsTrigger>
          </TabsList>

          {/* Real Trades Tab */}
          <TabsContent value="real" className="mt-6 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <Card className="bg-card border-white/10">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">{language === 'fr' ? 'Total' : 'Total'}</div>
                  <div className="text-2xl font-mono font-bold mt-1">{filteredClosedTrades.length}</div>
                </CardContent>
              </Card>
              <Card className="bg-card border-white/10">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">{language === 'fr' ? 'Win Rate' : 'Win Rate'}</div>
                  <div className="text-2xl font-mono font-bold mt-1 text-primary">{winRate}%</div>
                </CardContent>
              </Card>
              <Card className={`bg-card border-white/10 ${stats.pending > 0 ? 'ring-2 ring-orange-500/50' : ''}`}>
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <Hourglass className="w-4 h-4 text-orange-400" /> {language === 'fr' ? 'En attente' : 'Pending'}
                  </div>
                  <div className="text-2xl font-mono font-bold mt-1 text-orange-400">{stats.pending}</div>
                </CardContent>
              </Card>
              <Card className="bg-card border-white/10">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <Play className="w-4 h-4 text-yellow-400" /> {language === 'fr' ? 'En cours' : 'Active'}
                  </div>
                  <div className="text-2xl font-mono font-bold mt-1 text-yellow-400">{stats.active}</div>
                </CardContent>
              </Card>
              <Card className="bg-card border-white/10">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-primary" /> {language === 'fr' ? 'Gagnés' : 'Wins'}
                  </div>
                  <div className="text-2xl font-mono font-bold mt-1 text-primary">{winningTrades.length}</div>
                </CardContent>
              </Card>
              <Card className="bg-card border-white/10">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <XCircle className="w-4 h-4 text-destructive" /> {language === 'fr' ? 'Perdus' : 'Losses'}
                  </div>
                  <div className="text-2xl font-mono font-bold mt-1 text-destructive">{losingTrades.length}</div>
                </CardContent>
              </Card>
            </div>

            {/* Advanced Stats: Profit Factor, Avg Win, Avg Loss */}
            {(stats.wins > 0 || stats.losses > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-card border-white/10">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">{language === 'fr' ? 'Profit Factor' : 'Profit Factor'}</div>
                    <div className={`text-2xl font-mono font-bold mt-1 ${profitFactor >= 1.5 ? 'text-primary' : profitFactor >= 1 ? 'text-yellow-400' : 'text-destructive'}`}>
                      {profitFactor.toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {profitFactor >= 2 ? '🔥 Excellent' : profitFactor >= 1.5 ? '✅ Bon' : profitFactor >= 1 ? '⚠️ Moyen' : '❌ À améliorer'}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-white/10">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      {language === 'fr' ? 'Gain moyen' : 'Avg Win'}
                    </div>
                    <div className="text-2xl font-mono font-bold mt-1 text-primary">
                      +${avgWin.toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {language === 'fr' ? `sur ${winningTrades.length} trade${stats.wins > 1 ? 's' : ''} gagnant${stats.wins > 1 ? 's' : ''}` : `over ${winningTrades.length} winning trade${stats.wins > 1 ? 's' : ''}`}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-white/10">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <TrendingDown className="w-4 h-4 text-destructive" />
                      {language === 'fr' ? 'Perte moyenne' : 'Avg Loss'}
                    </div>
                    <div className="text-2xl font-mono font-bold mt-1 text-destructive">
                      -${avgLoss.toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {language === 'fr' ? `sur ${losingTrades.length} trade${stats.losses > 1 ? 's' : ''} perdant${stats.losses > 1 ? 's' : ''}` : `over ${losingTrades.length} losing trade${stats.losses > 1 ? 's' : ''}`}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Sub-filter tabs - Using extracted component */}
            <SubFilterTabs
              language={language}
              activeFilter={subFilter}
              onFilterChange={handleSubFilterChange}
              counts={{
                pending: stats.pending,
                active: stats.active,
                closed: closedTrades.length
              }}
              showAnalyticsButton={subFilter === 'closed' && closedTrades.length > 0}
              showAnalytics={showAnalytics}
              onToggleAnalytics={() => setShowAnalytics(!showAnalytics)}
              variant="real"
            />

            {/* === PANNEAU D'ANALYSE STATISTIQUE AVANCÉE - Using extracted component === */}
            {showAnalytics && subFilter === 'closed' && (
              <>
                <AnalyticsFiltersPanel
                  language={language}
                  analyticsFilters={analyticsFilters}
                  toggleFilter={toggleAnalyticsFilter}
                  resetFilters={resetAnalyticsFilters}
                  dateFilter={dateFilter}
                  setDateFilter={setDateFilter}
                  variant="real"
                />
                
                {/* Filtered Stats Display */}
                <Card className="bg-card/50 border-white/10">
                  <CardContent className="pt-4">
                    {/* Active Filters Badge Display - Using extracted component */}
                    <ActiveFiltersBadge
                      language={language}
                      activeFilters={getActiveFilterLabels()}
                      onRemoveFilter={handleRemoveFilter}
                      onResetAll={resetAnalyticsFilters}
                      variant="green"
                    />
                    
                    <div className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      {hasActiveAnalyticsFilters() 
                        ? (language === 'fr' ? `${filteredClosedTrades.length} trades correspondent aux filtres` : `${filteredClosedTrades.length} trades match filters`)
                        : (language === 'fr' ? `${filteredClosedTrades.length} trades terminés au total` : `${filteredClosedTrades.length} closed trades total`)
                      }
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

        {/* PNL Summary - Using extracted component */}
        {closedTrades.length > 0 && (
          <PnlSummaryCard
            language={language}
            totalGains={totalGains}
            totalLosses={totalLosses}
            totalPnl={totalPnl}
            winRate={winRate}
            bestTrade={bestTrade}
            worstTrade={worstTrade}
            variant="real"
          />
        )}

        {/* Summary Stats - USE SINGLE SOURCE realTradesStats */}
        {(() => {
          const statsForSummary = realTradesStats; // USE GLOBAL SINGLE SOURCE
          
          if (filteredTrades.length === 0) return null;
          
          const totals = calculateTotals(filteredTrades);
          
          return (
          <Card className="bg-card border-white/10 mb-4" data-testid="summary-stats">
            <CardContent className="p-4">
              {(() => {
                // Trades EN COURS (actifs + pending)
                if (subFilter === 'active') {
                  return (
                    <div className="grid grid-cols-2 gap-4">
                      {/* PnL Non Réalisé */}
                      <div 
                        className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 cursor-pointer hover:bg-blue-500/20 transition-colors"
                        onClick={() => setPnlHistoryDialog('unrealized')}
                        data-testid="unrealized-card"
                      >
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Activity className="w-3 h-3" />
                          {language === 'fr' ? 'PnL Non Réalisé' : 'Unrealized PnL'}
                        </div>
                        <div className={`font-mono font-bold text-lg ${totals.unrealizedPnl >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                          {totals.unrealizedPnl >= 0 ? '+' : ''}{totals.unrealizedPnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {language === 'fr' ? 'Position restante non sécurisée' : 'Remaining unsecured position'}
                        </div>
                      </div>
                      {/* Gains Partiels */}
                      <div 
                        className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 cursor-pointer hover:bg-green-500/20 transition-colors"
                        onClick={() => setPnlHistoryDialog('partial')}
                        data-testid="partial-gains-card"
                      >
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          {language === 'fr' ? 'Gains Partiels' : 'Partial Gains'}
                        </div>
                        <div className="font-mono font-bold text-green-400 text-lg">
                          +{totals.partialTpGains.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {language === 'fr' ? 'TP touchés (sécurisés)' : 'TP hits (secured)'}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Trades TERMINÉS (WIN/LOSS)
                if (subFilter === 'closed') {
                  return (
                    <div className="grid grid-cols-3 gap-4">
                      {/* Gains Total */}
                      <div 
                        className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 cursor-pointer hover:bg-green-500/20 transition-colors"
                        onClick={() => setPnlHistoryDialog('gains')}
                        data-testid="gains-card"
                      >
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          {language === 'fr' ? 'Gains Total' : 'Total Gains'}
                        </div>
                        <div className="font-mono font-bold text-green-400 text-lg">
                          +${statsForSummary.totalGains.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {language === 'fr' ? 'Cliquez pour détails' : 'Click for details'}
                        </div>
                      </div>
                      {/* Pertes Total */}
                      <div 
                        className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 cursor-pointer hover:bg-red-500/20 transition-colors"
                        onClick={() => setPnlHistoryDialog('losses')}
                        data-testid="losses-card"
                      >
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <TrendingDown className="w-3 h-3" />
                          {language === 'fr' ? 'Pertes Total' : 'Total Losses'}
                        </div>
                        <div className="font-mono font-bold text-red-400 text-lg">
                          -${statsForSummary.totalLosses.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {language === 'fr' ? 'Cliquez pour détails' : 'Click for details'}
                        </div>
                      </div>
                      {/* PnL Réalisé */}
                      <div 
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${statsForSummary.totalPnl >= 0 ? 'bg-primary/10 border-primary/20 hover:bg-primary/20' : 'bg-destructive/10 border-destructive/20 hover:bg-destructive/20'}`}
                        onClick={() => setPnlHistoryDialog('total')}
                        data-testid="total-pnl-card"
                      >
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {language === 'fr' ? 'PnL Réalisé' : 'Realized PnL'}
                        </div>
                        <div className={`font-mono font-bold text-lg ${statsForSummary.totalPnl >= 0 ? 'text-primary' : 'text-destructive'}`}>
                          {statsForSummary.totalPnl >= 0 ? '+' : ''}{statsForSummary.totalPnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {language === 'fr' ? 'Gains - Pertes' : 'Gains - Losses'}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // TOUS les trades (all)
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Gains Total (clôturés) */}
                    <div 
                      className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 cursor-pointer hover:bg-green-500/20 transition-colors"
                      onClick={() => setPnlHistoryDialog('gains')}
                      data-testid="gains-card"
                    >
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        {language === 'fr' ? 'Gains Clôturés' : 'Closed Gains'}
                      </div>
                      <div className="font-mono font-bold text-green-400 text-lg">
                        +${totals.closedGains.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </div>
                    </div>
                    {/* Pertes Total (clôturées) */}
                    <div 
                      className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 cursor-pointer hover:bg-red-500/20 transition-colors"
                      onClick={() => setPnlHistoryDialog('losses')}
                      data-testid="losses-card"
                    >
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <TrendingDown className="w-3 h-3" />
                        {language === 'fr' ? 'Pertes Clôturées' : 'Closed Losses'}
                      </div>
                      <div className="font-mono font-bold text-red-400 text-lg">
                        -${totals.closedLosses.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </div>
                    </div>
                    {/* Gains Partiels */}
                    <div 
                      className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 cursor-pointer hover:bg-emerald-500/20 transition-colors"
                      onClick={() => setPnlHistoryDialog('partial')}
                      data-testid="partial-gains-card"
                    >
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        {language === 'fr' ? 'TP Partiels' : 'Partial TP'}
                      </div>
                      <div className="font-mono font-bold text-emerald-400 text-lg">
                        +${totals.partialTpGains.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </div>
                    </div>
                    {/* PnL Non Réalisé */}
                    <div 
                      className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 cursor-pointer hover:bg-blue-500/20 transition-colors"
                      onClick={() => setPnlHistoryDialog('unrealized')}
                      data-testid="unrealized-card"
                    >
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Activity className="w-3 h-3" />
                        {language === 'fr' ? 'PnL Latent' : 'Unrealized'}
                      </div>
                      <div className={`font-mono font-bold text-lg ${totals.unrealizedPnl >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                        {totals.unrealizedPnl >= 0 ? '+' : ''}{totals.unrealizedPnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
          );
        })()}

        {/* Trades List */}
        <Card className="bg-card border-white/10" data-testid="trades-list">
          <CardContent className="p-4">
            {filteredTrades.length === 0 ? (
              <div className="text-center py-16">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground">
                  {language === 'fr' ? 'Aucun trade dans cette categorie' : 'No trades in this category'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Sort toggle for closed trades — chronological order */}
                {subFilter === 'closed' && filteredTrades.length > 1 && (
                  <div className="flex items-center justify-end mb-2">
                    <button
                      type="button"
                      onClick={() => setClosedSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                      data-testid="closed-trades-sort-toggle"
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md border border-white/10 hover:border-white/20"
                      title={closedSortOrder === 'desc'
                        ? (language === 'fr' ? 'Actuellement : plus récents en premier. Cliquer pour inverser.' : 'Currently: most recent first. Click to reverse.')
                        : (language === 'fr' ? 'Actuellement : plus anciens en premier. Cliquer pour inverser.' : 'Currently: oldest first. Click to reverse.')}
                    >
                      {closedSortOrder === 'desc' ? (
                        <>
                          <ArrowDownUp className="w-3 h-3" />
                          {language === 'fr' ? 'Plus récents d\'abord' : 'Newest first'}
                        </>
                      ) : (
                        <>
                          <ArrowDownUp className="w-3 h-3 rotate-180" />
                          {language === 'fr' ? 'Plus anciens d\'abord' : 'Oldest first'}
                        </>
                      )}
                    </button>
                  </div>
                )}
                {/* Apply pagination only for closed trades */}
                {(subFilter === 'closed' ? filteredTrades.slice(0, visibleClosedCount) : filteredTrades).map((trade, index) => {
                  const isExpanded = expandedTrades[trade.id];
                  
                  // Use helper function for case-insensitive price lookup
                  const currentPrice = getPriceForSymbol(currentPrices, trade.symbol);
                  
                  const entry = trade.entry_price;
                  
                  // WARNING: Flag trades with invalid entry_price
                  const hasInvalidEntry = !entry || entry <= 0;
                  if (hasInvalidEntry && (trade.status === 'active' || trade.status === 'pending')) {
                    console.warn('[Invalid Trade]', {
                      tradeId: trade.id,
                      tradeSymbol: trade.symbol,
                      entryPrice: entry,
                      status: trade.status,
                      suggestion: 'This trade has invalid entry_price - consider deleting or editing it'
                    });
                  }
                  
                  // DEBUG: Log why price might be missing for active trades
                  if ((trade.status === 'active' || trade.status === 'pending') && currentPrice === undefined) {
                    console.warn('[Missing Price]', {
                      tradeSymbol: trade.symbol,
                      tradeStatus: trade.status,
                      allPriceKeys: Object.keys(currentPrices),
                      suggestion: 'Check if backend check-prices returns this symbol'
                    });
                  }
                  const sl = trade.stop_loss;
                  const tp1 = trade.take_profit_1;
                  const tp2 = trade.take_profit_2;
                  const tp100 = trade.take_profit_100;
                  const leverage = trade.leverage || 1;
                  const posSize = trade.position_size || 0;
                  const isLong = trade.direction === 'LONG';
                  const isClosed = trade.status === 'WIN' || trade.status === 'LOSS';
                  const isPending = trade.status === 'pending';
                  const isWin = trade.status === 'WIN';
                  
                  // Determine entry type for display
                  let tradeEntryType = trade.auto_test_entry_type || trade.entry_type;
                  if (!tradeEntryType) {
                    if (trade.created_at_price !== null && trade.created_at_price !== undefined) {
                      tradeEntryType = trade.entry_price === trade.created_at_price ? 'market' : 'optimal';
                    } else {
                      tradeEntryType = trade.original_status === 'pending' ? 'optimal' : 'market';
                    }
                  }
                  
                  // For closed trades, use exit_price to determine TP hits
                  const priceToCheck = isClosed ? trade.exit_price : currentPrice;
                  
                  // Check which TPs are hit (never for pending trades)
                  // For WIN trades, ALL TPs are considered hit (since WIN = TP100 reached = all TPs reached)
                  // IMPORTANT: Use warnings_sent from backend (persisted) OR current price check
                  const warnings = trade.warnings_sent || {};
                  
                  // TP hit = either saved in DB (warnings.tp1_hit) OR current price is above TP
                  const tp1HitFromWarnings = warnings.tp1_hit || false;
                  const tp2HitFromWarnings = warnings.tp2_hit || false;
                  const tp100HitFromWarnings = warnings.tp100_hit || false;
                  
                  const tp1HitFromPrice = !isPending && tp1 && priceToCheck && (isLong ? priceToCheck >= tp1 : priceToCheck <= tp1);
                  const tp2HitFromPrice = !isPending && tp2 && priceToCheck && (isLong ? priceToCheck >= tp2 : priceToCheck <= tp2);
                  const tp100HitFromPrice = !isPending && tp100 && priceToCheck && (isLong ? priceToCheck >= tp100 : priceToCheck <= tp100);
                  
                  // Final TP hit status: only from warnings_sent (persisted by backend) or from
                  // current price (active trades only). We do NOT blanket-mark all TPs as hit
                  // for WIN trades — a WIN can happen with only 1 TP secured (rest at market).
                  const tp1Hit = tp1HitFromWarnings || (!isClosed && tp1HitFromPrice);
                  const tp2Hit = tp2HitFromWarnings || (!isClosed && tp2HitFromPrice);
                  const tp100Hit = tp100HitFromWarnings || (!isClosed && tp100HitFromPrice);
                  
                  // Calculate unrealized PnL for active trades
                  // IMPORTANT: PnL latent = gain non sécurisé sur la portion restante
                  // Le PnL latent doit être plafonné par les TPs non encore touchés
                  let unrealizedPnl = 0;
                  let unrealizedPnlPct = 0;
                  
                  // For partial gains calculation, use the same TP hit status
                  let tp1ShouldBeHit = tp1Hit;
                  let tp2ShouldBeHit = tp2Hit;
                  
                  // Calculate partial gains (gains sécurisés des TPs touchés)
                  let partialGainsOnTrade = 0;
                  if (tp1ShouldBeHit && tp1 && entry > 0) {
                    const tp1DistPctCalc = Math.abs(tp1 - entry) / entry * 100;
                    partialGainsOnTrade += (tp1DistPctCalc / 100) * posSize * leverage * 0.25;
                  }
                  if (tp2ShouldBeHit && tp2 && entry > 0) {
                    const tp2DistPctCalc = Math.abs(tp2 - entry) / entry * 100;
                    partialGainsOnTrade += (tp2DistPctCalc / 100) * posSize * leverage * 0.25;
                  }
                  
                  if (trade.status === 'active' && currentPrice && entry) {
                    // Calculer le gain % au prix actuel
                    if (isLong) {
                      unrealizedPnlPct = ((currentPrice - entry) / entry) * 100 * leverage;
                    } else {
                      unrealizedPnlPct = ((entry - currentPrice) / entry) * 100 * leverage;
                    }
                    
                    // CORRECTION: Le PnL latent doit être calculé portion par portion
                    // et chaque portion est plafonnée par son TP correspondant
                    
                    // Portion TP1 (25%) - si pas touché, PnL plafonné à TP1 (si TP1 défini)
                    let pnlTp1Portion = 0;
                    if (!tp1ShouldBeHit) {
                      const tp1MaxPct = tp1 && entry > 0 ? Math.abs(tp1 - entry) / entry * 100 * leverage : Infinity;
                      const cappedPct = Math.min(unrealizedPnlPct, tp1MaxPct);
                      pnlTp1Portion = (cappedPct / 100) * posSize * 0.25;
                    }
                    
                    // Portion TP2 (25%) - si pas touché, PnL plafonné à TP2 (si TP2 défini)
                    let pnlTp2Portion = 0;
                    if (!tp2ShouldBeHit) {
                      const tp2MaxPct = tp2 && entry > 0 ? Math.abs(tp2 - entry) / entry * 100 * leverage : Infinity;
                      const cappedPct = Math.min(unrealizedPnlPct, tp2MaxPct);
                      pnlTp2Portion = (cappedPct / 100) * posSize * 0.25;
                    }
                    
                    // Portion TP100 (50%) - PnL complet (pas de plafond)
                    const pnlTp100Portion = (unrealizedPnlPct / 100) * posSize * 0.50;
                    
                    // PnL latent = somme des portions non sécurisées
                    // LIQUIDATION: une perte latente ne peut JAMAIS dépasser la marge investie
                    unrealizedPnl = Math.max(pnlTp1Portion + pnlTp2Portion + pnlTp100Portion, -posSize);
                    // Le % latent est aussi plafonné à -100% (liquidation)
                    unrealizedPnlPct = Math.max(unrealizedPnlPct, -100);
                  }
                  
                  // Calculate total gain for WIN trades (with partial exit strategy)
                  // For WIN = all TPs hit = 25% + 25% + 50% = 100% of position
                  // Note: posSize here is trade.position_size which is actually the MARGIN
                  const marge = posSize; // position_size IS the margin
                  const positionValue = marge * leverage;
                  const slDistPct = entry > 0 ? Math.abs(sl - entry) / entry * 100 : 0;
                  const tp1DistPct = tp1 && entry > 0 ? Math.abs(tp1 - entry) / entry * 100 : 0;
                  const tp2DistPct = tp2 && entry > 0 ? Math.abs(tp2 - entry) / entry * 100 : 0;
                  const tp100DistPct = tp100 && entry > 0 ? Math.abs(tp100 - entry) / entry * 100 : 0;
                  const gainTp1 = (tp1DistPct / 100) * marge * leverage * 0.25;
                  const gainTp2 = (tp2DistPct / 100) * marge * leverage * 0.25;
                  const gainTp100 = (tp100DistPct / 100) * marge * leverage * 0.50;
                  const gainTotal = gainTp1 + gainTp2 + gainTp100;
                  const gainTotalPct = marge > 0 ? (gainTotal / marge) * 100 : 0;
                  
                  // For display: WIN trades use projected total gain only if the trade
                  // was auto-closed at TP100 (all TPs hit). Otherwise the backend's
                  // `pnl` field is the source of truth (secured + remaining portion).
                  const allTpsHit = trade.warnings_sent?.tp1_hit && trade.warnings_sent?.tp2_hit && trade.warnings_sent?.tp100_hit;
                  const displayPnl = (isWin && allTpsHit && trade.pnl == null) ? gainTotal : trade.pnl;
                  const displayPnlPct = (isWin && allTpsHit && trade.pnl_percentage == null) ? gainTotalPct : trade.pnl_percentage;
                  const isHighlighted = trade.id === highlightedTradeId;
                  
                  return (
                    <div
                      key={trade.id}
                      ref={isHighlighted ? highlightedTradeRef : null}
                      className={`rounded-xl transition-all overflow-hidden ${
                        isHighlighted 
                          ? 'bg-primary/20 ring-2 ring-primary animate-pulse' 
                          : 'bg-secondary/30 hover:bg-secondary/40'
                      }`}
                      data-testid={`trade-${trade.id}`}
                    >
                      {/* Compact Header - Always Visible */}
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => toggleExpanded(trade.id)}
                      >
                        <div className="flex items-center justify-between gap-4">
                          {/* Left: Direction + Symbol + Status */}
                          <div className="flex items-center gap-3">
                            {/* Expand/Collapse Button */}
                            <button className="p-1 rounded hover:bg-white/10 transition-colors">
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              )}
                            </button>
                            
                            {/* Direction Icon */}
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              trade.direction === 'LONG' 
                                ? 'bg-primary/20' 
                                : 'bg-destructive/20'
                            }`}>
                              {trade.direction === 'LONG' ? (
                                <TrendingUp className="w-5 h-5 text-primary" />
                              ) : (
                                <TrendingDown className="w-5 h-5 text-destructive" />
                              )}
                            </div>

                            {/* Symbol + Badges */}
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-lg">{getDisplayName(trade.symbol, language)}</span>
                                <span className="text-xs text-muted-foreground font-mono">({trade.symbol})</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                                  trade.direction === 'LONG' 
                                    ? 'bg-primary/20 text-primary' 
                                    : 'bg-destructive/20 text-destructive'
                                }`}>
                                  {trade.direction}
                                </span>
                                {trade.trade_type && (
                                  <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                                    trade.trade_type === 'SWING' 
                                      ? 'bg-blue-500/20 text-blue-400' 
                                      : 'bg-orange-500/20 text-orange-400'
                                  }`}>
                                    {trade.trade_type}
                                  </span>
                                )}
                                {trade.leverage && trade.leverage > 1 && (
                                  <span className="px-2 py-0.5 rounded text-xs font-mono bg-yellow-500/20 text-yellow-400">
                                    {trade.leverage}x
                                  </span>
                                )}
                                {trade.profile && (
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    trade.profile === 'conservative' ? 'bg-blue-500/20 text-blue-400' :
                                    trade.profile === 'balanced' ? 'bg-green-500/20 text-green-400' :
                                    trade.profile === 'dynamic' ? 'bg-orange-500/20 text-orange-400' :
                                    'bg-purple-500/20 text-purple-400'
                                  }`}>
                                    {trade.profile === 'conservative' ? '🛡️ Prudent' :
                                     trade.profile === 'balanced' ? '⚖️ Équilibré' :
                                     trade.profile === 'dynamic' ? '🔥 Dynamique' :
                                     trade.profile}
                                  </span>
                                )}
                                {/* Badge Type d'entrée */}
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  tradeEntryType === 'market' 
                                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                                    : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                }`}>
                                  {tradeEntryType === 'market' ? '⚡ Marché' : '🎯 Optimal'}
                                </span>
                                {/* Badge Source (Auto-Scan vs Manuel) - Only show for actual Auto-Scan trades */}
                                {trade.auto_scan_created && (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-500/20 text-violet-400 border border-violet-500/30">
                                    🤖 Auto-Scan
                                  </span>
                                )}
                                <span className={`px-2 py-0.5 rounded text-xs font-mono ${getStatusColor(trade.status)}`}>
                                  {getStatusLabel(trade.status)}
                                </span>
                                {/* Date de clôture pour les trades terminés */}
                                {(trade.status === 'WIN' || trade.status === 'LOSS') && trade.closed_at && (
                                  <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-secondary/50 text-muted-foreground">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(trade.closed_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {new Date(trade.created_at).toLocaleDateString('fr-FR')}
                              </div>
                            </div>
                          </div>

                          {/* Center: Current Price (for active/pending) */}
                          {(trade.status === 'active' || trade.status === 'pending') && (
                            <div className="hidden md:flex items-center gap-4">
                              {currentPrice ? (
                                <div className="text-center">
                                  <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Prix actuel' : 'Current'}</div>
                                  <div className="font-mono font-bold text-lg flex items-center gap-1">
                                    <Activity className="w-4 h-4 text-blue-400" />
                                    ${formatPrice(currentPrice)}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Prix actuel' : 'Current'}</div>
                                  <div className="font-mono text-muted-foreground">—</div>
                                </div>
                              )}
                              
                              {/* Unrealized PnL for active trades */}
                              {trade.status === 'active' && currentPrice && (
                                <div className="text-center">
                                  <div className="text-xs text-muted-foreground">{language === 'fr' ? 'PnL Latent' : 'Unrealized'}</div>
                                  <div className={`font-mono font-bold ${unrealizedPnl >= 0 ? 'text-purple-400' : 'text-orange-400'}`}>
                                    {unrealizedPnl >= 0 ? '+' : ''}{unrealizedPnl.toFixed(2)}$
                                    <span className="text-xs ml-1">({unrealizedPnlPct >= 0 ? '+' : ''}{unrealizedPnlPct.toFixed(1)}%)</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Partial Gains for active trades with TP hits */}
                              {trade.status === 'active' && partialGainsOnTrade > 0 && (
                                <div className="text-center">
                                  <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Gains Sécurisés' : 'Secured'}</div>
                                  <div className="font-mono font-bold text-green-400">
                                    +{partialGainsOnTrade.toFixed(2)}$
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Right: PnL for closed trades OR Quick TPs Status */}
                          <div className="flex items-center gap-3">
                            {/* TP Status Indicators */}
                            {(trade.status === 'active') && (
                              <div className="hidden sm:flex items-center gap-1">
                                {tp1 && (
                                  <div className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                                    tp1Hit 
                                      ? 'bg-green-600 dark:bg-green-500 text-white ring-2 ring-green-600 dark:ring-green-400 ring-offset-2 ring-offset-background shadow-md' 
                                      : 'bg-muted/30 text-muted-foreground'
                                  }`}>
                                    TP1 {tp1Hit && '✓'}
                                  </div>
                                )}
                                {tp2 && (
                                  <div className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                                    tp2Hit 
                                      ? 'bg-green-600 dark:bg-green-500 text-white ring-2 ring-green-600 dark:ring-green-400 ring-offset-2 ring-offset-background shadow-md' 
                                      : 'bg-muted/30 text-muted-foreground'
                                  }`}>
                                    TP2 {tp2Hit && '✓'}
                                  </div>
                                )}
                                {tp100 && (
                                  <div className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                                    tp100Hit 
                                      ? 'bg-emerald-600 dark:bg-emerald-500 text-white ring-2 ring-emerald-600 dark:ring-emerald-400 ring-offset-2 ring-offset-background shadow-md animate-pulse' 
                                      : 'bg-muted/30 text-muted-foreground'
                                  }`}>
                                    TP100 {tp100Hit && '✓'}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* PnL for closed trades */}
                            {(trade.status === 'WIN' || trade.status === 'LOSS') && (displayPnl !== undefined || trade.pnl !== undefined) && (
                              <div className="text-right">
                                <div className={`font-mono font-bold text-lg ${(displayPnl || 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                                  {(displayPnl || 0) >= 0 ? '+' : ''}{(displayPnl || 0).toFixed(2)}$
                                </div>
                                <div className={`text-xs ${(displayPnlPct || 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                                  {(displayPnlPct || 0) >= 0 ? '+' : ''}{(displayPnlPct || 0).toFixed(2)}%
                                </div>
                              </div>
                            )}
                            
                            {/* Actions Menu */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" data-testid={`trade-menu-${trade.id}`}>
                                  <MoreVertical className="w-5 h-5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-card border-white/10">
                                {trade.status === 'pending' && (
                                  <DropdownMenuItem onClick={() => handleCloseTrade(trade, 'active')}>
                                    <Play className="w-4 h-4 mr-2 text-yellow-400" />
                                    {language === 'fr' ? 'Activer' : 'Activate'}
                                  </DropdownMenuItem>
                                )}
                                {(trade.status === 'pending' || trade.status === 'active') && (
                                  <DropdownMenuItem 
                                    onClick={() => handleCloseTrade(trade, 'CLOSE')}
                                    className="text-primary focus:text-primary"
                                    data-testid="close-trade-market"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    {language === 'fr' ? 'Clôturer au marché' : 'Close at market'}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem 
                                  onClick={() => deleteTrade(trade.id)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Supprimer' : 'Delete'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        
                        {/* Mobile: Current Price + PnL */}
                        {(trade.status === 'active' || trade.status === 'pending') && currentPrice && (
                          <div className="md:hidden mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                            <div>
                              <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Prix actuel' : 'Current'}: </span>
                              <span className="font-mono font-bold">${formatPrice(currentPrice)}</span>
                            </div>
                            {trade.status === 'active' && (
                              <div className={`font-mono font-bold ${unrealizedPnl >= 0 ? 'text-primary' : 'text-destructive'}`}>
                                {unrealizedPnl >= 0 ? '+' : ''}{unrealizedPnl.toFixed(2)}$ ({unrealizedPnlPct >= 0 ? '+' : ''}{unrealizedPnlPct.toFixed(1)}%)
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-white/5">
                          {(() => {
                            const entry = trade.entry_price || 0;
                            const sl = trade.stop_loss || 0;
                            const tp1 = trade.take_profit_1;
                            const tp2 = trade.take_profit_2;
                            const tp100 = trade.take_profit_100;
                            const leverage = trade.leverage || 1;
                            const marge = trade.position_size || 0;
                            const positionValue = marge * leverage;
                            const posSize = entry > 0 ? positionValue / entry : 0;
                            const isLong = trade.direction === 'LONG';
                            const currentPrice = getPriceForSymbol(currentPrices, trade.symbol);
                            const isClosed = trade.status === 'WIN' || trade.status === 'LOSS';
                            const isPending = trade.status === 'pending';
                            
                            // For closed trades, use exit_price to determine TP hits
                            // For active trades, use currentPrice
                            // For pending trades, TPs should NOT be marked as hit (trade not activated yet)
                            const priceToCheck = isClosed ? trade.exit_price : currentPrice;
                            
                            // Check TPs hit based on exit price (closed) or current price (active)
                            // For PENDING trades, TPs are never "hit" since trade isn't active
                            // For WIN trades, ALL TPs are considered hit (since WIN = TP100 reached = all TPs reached)
                            const isWin = trade.status === 'WIN';
                            const warnings = trade.warnings_sent || {};
                            
                            // TP hit = either saved in DB (warnings.tp1_hit) OR current price is above TP
                            const tp1HitFromWarnings = warnings.tp1_hit || false;
                            const tp2HitFromWarnings = warnings.tp2_hit || false;
                            const tp100HitFromWarnings = warnings.tp100_hit || false;
                            
                            const tp1HitFromPrice = !isPending && tp1 && priceToCheck && (isLong ? priceToCheck >= tp1 : priceToCheck <= tp1);
                            const tp2HitFromPrice = !isPending && tp2 && priceToCheck && (isLong ? priceToCheck >= tp2 : priceToCheck <= tp2);
                            const tp100HitFromPrice = !isPending && tp100 && priceToCheck && (isLong ? priceToCheck >= tp100 : priceToCheck <= tp100);
                            
                            // Final TP hit status: only from warnings_sent or current price
                            // (never force-checked just because status=WIN — a WIN can be a
                            // partial-only close where not all TPs were hit).
                            const tp1Hit = tp1HitFromWarnings || (!isClosed && tp1HitFromPrice);
                            const tp2Hit = tp2HitFromWarnings || (!isClosed && tp2HitFromPrice);
                            const tp100Hit = tp100HitFromWarnings || (!isClosed && tp100HitFromPrice);
                            
                            // Calculate distances (percentage without leverage)
                            const slDistPct = entry > 0 ? Math.abs(sl - entry) / entry * 100 : 0;
                            const tp1DistPct = tp1 && entry > 0 ? Math.abs(tp1 - entry) / entry * 100 : 0;
                            const tp2DistPct = tp2 && entry > 0 ? Math.abs(tp2 - entry) / entry * 100 : 0;
                            const tp100DistPct = tp100 && entry > 0 ? Math.abs(tp100 - entry) / entry * 100 : 0;
                            
                            // Calculate PnL at levels WITH PARTIAL EXIT STRATEGY (25% + 25% + 50%)
                            // TP1: 25% de la position, TP2: 25% de la position, TP100: 50% restants
                            const perteSL = (slDistPct / 100) * marge * leverage;
                            const gainTp1 = (tp1DistPct / 100) * marge * leverage * 0.25;  // 25%
                            const gainTp2 = (tp2DistPct / 100) * marge * leverage * 0.25;  // 25%
                            const gainTp100 = (tp100DistPct / 100) * marge * leverage * 0.50; // 50%
                            const gainTotal = gainTp1 + gainTp2 + gainTp100;  // Somme des 3 TPs
                            
                            // R:R basé sur la DISTANCE pure (TP100-Entry) / (Entry-SL)
                            // On utilise le risk_reward_ratio du backend s'il existe, sinon on calcule
                            const rr = trade.risk_reward_ratio || (tp100DistPct > 0 && slDistPct > 0 ? tp100DistPct / slDistPct : 0);
                            
                            // Liquidation price
                            let liqPrice = 0;
                            if (leverage > 1) {
                              liqPrice = isLong 
                                ? entry * (1 - 1/leverage)
                                : entry * (1 + 1/leverage);
                            }
                            const slAfterLiq = leverage > 1 && ((isLong && sl < liqPrice) || (!isLong && sl > liqPrice));
                            
                            return (
                              <div className="space-y-4 mt-4">
                                {/* Grid Stats */}
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                  <div className="p-3 rounded-lg bg-secondary/50">
                                    <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Marge' : 'Margin'}</div>
                                    <div className="font-mono font-bold text-yellow-400">${marge.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                                  </div>
                                  <div className="p-3 rounded-lg bg-secondary/50">
                                    <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Position' : 'Position'}</div>
                                    <div className="font-mono font-bold">${positionValue.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                                  </div>
                                  <div className="p-3 rounded-lg bg-secondary/50">
                                    <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Quantite' : 'Qty'}</div>
                                    <div className="font-mono font-bold">{posSize.toFixed(6)}</div>
                                  </div>
                                  <div className="p-3 rounded-lg bg-secondary/50">
                                    <div className="text-xs text-muted-foreground">R:R</div>
                                    <div className="font-mono font-bold text-blue-400">{rr.toFixed(2)}:1</div>
                                  </div>
                                  <div className={`p-3 rounded-lg border ${slAfterLiq ? 'bg-red-900/30 border-red-500/50' : 'bg-destructive/10 border-destructive/20'}`}>
                                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                                      {slAfterLiq && <AlertTriangle className="w-3 h-3 text-red-400" />}
                                      {language === 'fr' ? 'Perte SL' : 'SL Loss'}
                                    </div>
                                    {slAfterLiq ? (
                                      <div className="font-mono font-bold text-red-400 flex items-center gap-1">
                                        <AlertTriangle className="w-4 h-4" />
                                        {language === 'fr' ? 'Liquidé' : 'Liquidated'}
                                      </div>
                                    ) : (
                                      <div className="font-mono font-bold text-destructive">-${perteSL.toFixed(2)}</div>
                                    )}
                                  </div>
                                  <div className={`p-3 rounded-lg bg-gradient-to-r ${isClosed ? ((trade.pnl ?? 0) >= 0 ? 'from-green-500/20 to-emerald-500/20 border-green-500/30' : 'from-red-500/20 to-rose-500/20 border-red-500/30') : 'from-green-500/20 to-emerald-500/20 border-green-500/30'} border`}>
                                    <div className="text-xs text-muted-foreground">
                                      {isClosed
                                        ? (language === 'fr' ? 'Gain / Perte réel' : 'Actual P&L')
                                        : (language === 'fr' ? 'Gain Total max' : 'Max Total Profit')}
                                    </div>
                                    <div className={`font-mono font-bold ${isClosed ? ((trade.pnl ?? 0) >= 0 ? 'text-green-400' : 'text-red-400') : 'text-green-400'}`}>
                                      {isClosed
                                        ? `${(trade.pnl ?? 0) >= 0 ? '+' : ''}$${(trade.pnl ?? 0).toFixed(2)}`
                                        : `+$${gainTotal.toFixed(2)}`}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Avertissement frais */}
                                <p className="text-xs text-muted-foreground/60 italic">
                                  {language === 'fr' 
                                    ? '* Gains approximatifs, frais d\'exchange non inclus' 
                                    : '* Approximate profits, exchange fees not included'}
                                </p>
                                
                                {/* Price Levels with TP hit indicators */}
                                <div className="p-3 rounded-lg bg-secondary/30">
                                  <div className="text-xs text-muted-foreground mb-2">{language === 'fr' ? 'Niveaux de prix' : 'Price Levels'}</div>
                                  <div className="flex items-center justify-between text-sm flex-wrap gap-2">
                                    {/* Liquidation */}
                                    {liqPrice > 0 && (
                                      <div className={`p-2 rounded ${slAfterLiq ? 'bg-red-900/50 border-2 border-red-500' : 'bg-red-500/10'}`}>
                                        <div className="text-xs text-red-400">Liq.</div>
                                        <div className="font-mono font-bold text-red-400">${formatPrice(liqPrice)}</div>
                                        <div className="text-xs font-mono text-red-400">-${marge.toFixed(2)}</div>
                                      </div>
                                    )}
                                    
                                    {/* SL */}
                                    <div className="p-2 rounded bg-destructive/10">
                                      <div className="text-xs text-destructive">SL</div>
                                      <div className="font-mono font-bold text-destructive">${formatPrice(sl)}</div>
                                    </div>
                                    
                                    {/* Entry */}
                                    <div className="p-2 rounded bg-white/10">
                                      <div className="text-xs text-muted-foreground">Entry</div>
                                      <div className="font-mono font-bold">${formatPrice(entry)}</div>
                                    </div>
                                    
                                    {/* TP1 */}
                                    {tp1 && (
                                      <div className={`p-2 rounded transition-all ${
                                        tp1Hit 
                                          ? 'bg-green-600 dark:bg-green-500 text-white ring-2 ring-green-500 dark:ring-green-400 ring-offset-2 ring-offset-background shadow-lg' 
                                          : 'bg-primary/10'
                                      }`}>
                                        <div className={`text-xs font-medium ${tp1Hit ? 'text-white' : 'text-primary/70'}`}>TP1 (25%) {tp1Hit && '✓'}</div>
                                        <div className={`font-mono font-bold ${tp1Hit ? 'text-white' : 'text-primary/70'}`}>${formatPrice(tp1)}</div>
                                        <div className={`text-xs ${tp1Hit ? 'text-green-100' : 'text-muted-foreground'}`}>+${gainTp1.toFixed(2)}</div>
                                      </div>
                                    )}
                                    
                                    {/* TP2 */}
                                    {tp2 && (
                                      <div className={`p-2 rounded transition-all ${
                                        tp2Hit 
                                          ? 'bg-green-600 dark:bg-green-500 text-white ring-2 ring-green-500 dark:ring-green-400 ring-offset-2 ring-offset-background shadow-lg' 
                                          : 'bg-primary/10'
                                      }`}>
                                        <div className={`text-xs font-medium ${tp2Hit ? 'text-white' : 'text-primary/80'}`}>TP2 (50%) {tp2Hit && '✓'}</div>
                                        <div className={`font-mono font-bold ${tp2Hit ? 'text-white' : 'text-primary/80'}`}>${formatPrice(tp2)}</div>
                                        <div className={`text-xs ${tp2Hit ? 'text-green-100' : 'text-muted-foreground'}`}>+${gainTp2.toFixed(2)}</div>
                                      </div>
                                    )}
                                    
                                    {/* TP100 */}
                                    {tp100 && (
                                      <div className={`p-2 rounded border transition-all ${
                                        tp100Hit 
                                          ? 'bg-emerald-600 dark:bg-emerald-500 text-white ring-2 ring-emerald-500 dark:ring-emerald-400 ring-offset-2 ring-offset-background shadow-lg animate-pulse' 
                                          : 'bg-primary/20 border-primary/30'
                                      }`}>
                                        <div className={`text-xs font-medium ${tp100Hit ? 'text-white' : 'text-primary'}`}>TP100% (50%) {tp100Hit && '✓'}</div>
                                        <div className={`font-mono font-bold ${tp100Hit ? 'text-white' : 'text-primary'}`}>${formatPrice(tp100)}</div>
                                        <div className={`text-xs ${tp100Hit ? 'text-emerald-100' : 'text-primary/70'}`}>+${gainTp100.toFixed(2)}</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Liquidation Warning */}
                                {slAfterLiq && (
                                  <div className="p-3 rounded-lg bg-red-900/30 border border-red-500/50 flex items-start gap-2">
                                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <div className="font-medium text-red-400">
                                        {language === 'fr' ? 'Attention : Liquidation avant SL' : 'Warning: Liquidation before SL'}
                                      </div>
                                      <div className="text-sm text-red-300/80">
                                        {language === 'fr' 
                                          ? `Avec un levier de ${leverage}x, liquidation a $${liqPrice.toFixed(2)} avant le SL.`
                                          : `With ${leverage}x leverage, liquidation at $${liqPrice.toFixed(2)} before SL.`
                                        }
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Notes */}
                                {trade.notes && (
                                  <div className="p-3 rounded-lg bg-secondary/30">
                                    <div className="text-xs text-muted-foreground mb-1">Notes</div>
                                    <p className="text-sm">{trade.notes}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Load More Button for Closed Trades */}
            {subFilter === 'closed' && filteredTrades.length > visibleClosedCount && (
              <div className="mt-4 text-center">
                <Button
                  variant="outline"
                  onClick={() => setVisibleClosedCount(prev => prev + TRADES_PER_PAGE)}
                  className="w-full max-w-xs border-primary/30 text-primary hover:bg-primary/10"
                  data-testid="load-more-trades"
                >
                  <ChevronDown className="w-4 h-4 mr-2" />
                  {language === 'fr' 
                    ? `Voir plus (${Math.min(TRADES_PER_PAGE, filteredTrades.length - visibleClosedCount)} sur ${filteredTrades.length - visibleClosedCount} restants)`
                    : `Load more (${Math.min(TRADES_PER_PAGE, filteredTrades.length - visibleClosedCount)} of ${filteredTrades.length - visibleClosedCount} remaining)`
                  }
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {language === 'fr' 
                    ? `Affichage de ${visibleClosedCount} sur ${filteredTrades.length} trades`
                    : `Showing ${visibleClosedCount} of ${filteredTrades.length} trades`
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Paper Trading Tab */}
          <TabsContent value="paper" className="mt-6 space-y-6">
            {/* Paper Portfolio Summary - Using extracted component */}
            {paperPortfolio && (
              <PortfolioSummaryCard
                language={language}
                portfolio={paperPortfolio}
                totalPnl={paperTotalPnl}
                winRate={paperWinRate}
                wins={paperWinningTrades.length}
                losses={paperLosingTrades.length}
                hasActiveFilters={hasActiveAnalyticsFilters()}
                onReset={() => setShowResetConfirm(true)}
                variant="paper"
              />
            )}

            {/* Paper Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <Card className="bg-card border-purple-500/20">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">{language === 'fr' ? 'Total' : 'Total'}</div>
                  <div className="text-2xl font-mono font-bold mt-1">{paperTrades.length}</div>
                </CardContent>
              </Card>
              <Card className="bg-card border-purple-500/20">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">{language === 'fr' ? 'Win Rate' : 'Win Rate'}</div>
                  <div className="text-2xl font-mono font-bold mt-1 text-purple-400">
                    {paperWinRate}%
                  </div>
                </CardContent>
              </Card>
              <Card className={`bg-card border-purple-500/20 ${paperPendingTrades.length > 0 ? 'ring-2 ring-orange-500/50' : ''}`}>
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <Hourglass className="w-4 h-4 text-orange-400" /> {language === 'fr' ? 'En attente' : 'Pending'}
                  </div>
                  <div className="text-2xl font-mono font-bold mt-1 text-orange-400">{paperPendingTrades.length}</div>
                </CardContent>
              </Card>
              <Card className="bg-card border-purple-500/20">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <Play className="w-4 h-4 text-yellow-400" /> {language === 'fr' ? 'En cours' : 'Active'}
                  </div>
                  <div className="text-2xl font-mono font-bold mt-1 text-yellow-400">
                    {paperActiveTrades.length}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card border-purple-500/20">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-primary" /> {language === 'fr' ? 'Gagnés' : 'Wins'}
                  </div>
                  <div className="text-2xl font-mono font-bold mt-1 text-primary">{paperWinningTrades.length}</div>
                </CardContent>
              </Card>
              <Card className="bg-card border-purple-500/20">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <XCircle className="w-4 h-4 text-destructive" /> {language === 'fr' ? 'Perdus' : 'Losses'}
                  </div>
                  <div className="text-2xl font-mono font-bold mt-1 text-destructive">{paperLosingTrades.length}</div>
                </CardContent>
              </Card>
            </div>

            {/* Advanced Stats for Paper Trading: Profit Factor, Avg Win, Avg Loss */}
            {(paperWinningTrades.length > 0 || paperLosingTrades.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-card border-purple-500/20">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">{language === 'fr' ? 'Profit Factor' : 'Profit Factor'}</div>
                    <div className={`text-2xl font-mono font-bold mt-1 ${paperProfitFactor >= 1.5 ? 'text-purple-400' : paperProfitFactor >= 1 ? 'text-yellow-400' : 'text-destructive'}`}>
                      {paperProfitFactor.toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {paperProfitFactor >= 2 ? '🔥 Excellent' : paperProfitFactor >= 1.5 ? '✅ Bon' : paperProfitFactor >= 1 ? '⚠️ Moyen' : '❌ À améliorer'}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-purple-500/20">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      {language === 'fr' ? 'Gain moyen' : 'Avg Win'}
                    </div>
                    <div className="text-2xl font-mono font-bold mt-1 text-primary">
                      +${paperAvgWin.toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {language === 'fr' ? `sur ${paperWinningTrades.length} trade${paperWinningTrades.length > 1 ? 's' : ''} gagnant${paperWinningTrades.length > 1 ? 's' : ''}` : `over ${paperWinningTrades.length} winning trade${paperWinningTrades.length > 1 ? 's' : ''}`}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-purple-500/20">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <TrendingDown className="w-4 h-4 text-destructive" />
                      {language === 'fr' ? 'Perte moyenne' : 'Avg Loss'}
                    </div>
                    <div className="text-2xl font-mono font-bold mt-1 text-destructive">
                      -${paperAvgLoss.toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {language === 'fr' ? `sur ${paperLosingTrades.length} trade${paperLosingTrades.length > 1 ? 's' : ''} perdant${paperLosingTrades.length > 1 ? 's' : ''}` : `over ${paperLosingTrades.length} losing trade${paperLosingTrades.length > 1 ? 's' : ''}`}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Sub-filter tabs for Paper - Using extracted component */}
            <SubFilterTabs
              language={language}
              activeFilter={subFilter}
              onFilterChange={handleSubFilterChange}
              counts={{
                pending: paperPendingTrades.length,
                active: paperActiveTrades.length,
                closed: paperClosedTrades.length
              }}
              showAnalyticsButton={subFilter === 'closed' && paperClosedTrades.length > 0}
              showAnalytics={showAnalytics}
              onToggleAnalytics={() => setShowAnalytics(!showAnalytics)}
              variant="paper"
            />

            {/* === PANNEAU D'ANALYSE STATISTIQUE AVANCÉE - PAPER TRADING - Using extracted component === */}
            {showAnalytics && subFilter === 'closed' && (
              <>
                <AnalyticsFiltersPanel
                  language={language}
                  analyticsFilters={analyticsFilters}
                  toggleFilter={toggleAnalyticsFilter}
                  resetFilters={resetAnalyticsFilters}
                  dateFilter={dateFilter}
                  setDateFilter={setDateFilter}
                  variant="paper"
                />
                
                {/* Filtered Stats Display */}
                <Card className="bg-card/50 border-purple-500/20">
                  <CardContent className="pt-4">
                    {/* Active Filters Badge Display - Using extracted component */}
                    <ActiveFiltersBadge
                      language={language}
                      activeFilters={getActiveFilterLabels()}
                      onRemoveFilter={handleRemoveFilter}
                      onResetAll={resetAnalyticsFilters}
                      variant="purple"
                    />
                    
                    <div className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      {hasActiveAnalyticsFilters() 
                        ? (language === 'fr' ? `${filteredPaperClosedTrades.length} trades correspondent aux filtres` : `${filteredPaperClosedTrades.length} trades match filters`)
                        : (language === 'fr' ? `${filteredPaperClosedTrades.length} trades terminés au total` : `${filteredPaperClosedTrades.length} closed trades total`)
                      }
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Summary Stats for Paper - USE SINGLE SOURCE paperTradesStats */}
            {(() => {
              const statsForPaperSummary = paperTradesStats; // USE GLOBAL SINGLE SOURCE
              
              if (paperTradesStats.total === 0 && subFilter !== 'active' && subFilter !== 'pending') return null;
              
              // FIX: Use paperTrades for paper trading totals, not filteredTrades (which is for real trades)
              const totals = calculateTotals(paperTrades);
              
              return (
              <Card className="bg-card border-purple-500/20 mb-4" data-testid="paper-summary-stats">
                <CardContent className="p-4">
                  {(() => {
                    // Trades EN COURS (actifs + pending)
                    if (subFilter === 'active') {
                      return (
                        <div className="grid grid-cols-2 gap-4">
                          {/* PnL Non Réalisé */}
                          <div 
                            className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20 cursor-pointer hover:bg-purple-500/20 transition-colors"
                            onClick={() => setPnlHistoryDialog('unrealized')}
                            data-testid="paper-unrealized-card"
                          >
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <Activity className="w-3 h-3" />
                              {language === 'fr' ? 'PnL Non Réalisé' : 'Unrealized PnL'}
                            </div>
                            <div className={`font-mono font-bold text-lg ${totals.unrealizedPnl >= 0 ? 'text-purple-400' : 'text-orange-400'}`}>
                              {totals.unrealizedPnl >= 0 ? '+' : ''}{totals.unrealizedPnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                              {language === 'fr' ? 'Position restante non sécurisée' : 'Remaining unsecured position'}
                            </div>
                          </div>
                          {/* Gains Partiels */}
                          <div 
                            className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 cursor-pointer hover:bg-green-500/20 transition-colors"
                            onClick={() => setPnlHistoryDialog('partial')}
                            data-testid="paper-partial-gains-card"
                          >
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {language === 'fr' ? 'Gains Partiels' : 'Partial Gains'}
                            </div>
                            <div className="font-mono font-bold text-green-400 text-lg">
                              +{totals.partialTpGains.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                              {language === 'fr' ? 'TP touchés (sécurisés)' : 'TP hits (secured)'}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Trades TERMINÉS (WIN/LOSS) - Use statsForPaperSummary for consistency
                    if (subFilter === 'closed') {
                      return (
                        <div className="grid grid-cols-3 gap-4">
                          {/* Gains Total */}
                          <div 
                            className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 cursor-pointer hover:bg-green-500/20 transition-colors"
                            onClick={() => setPnlHistoryDialog('gains')}
                            data-testid="paper-gains-card"
                          >
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              {language === 'fr' ? 'Gains Total' : 'Total Gains'}
                            </div>
                            <div className="font-mono font-bold text-green-400 text-lg">
                              +${statsForPaperSummary.totalGains.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                              {language === 'fr' ? 'Cliquez pour détails' : 'Click for details'}
                            </div>
                          </div>
                          {/* Pertes Total */}
                          <div 
                            className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 cursor-pointer hover:bg-red-500/20 transition-colors"
                            onClick={() => setPnlHistoryDialog('losses')}
                            data-testid="paper-losses-card"
                          >
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <TrendingDown className="w-3 h-3" />
                              {language === 'fr' ? 'Pertes Total' : 'Total Losses'}
                            </div>
                            <div className="font-mono font-bold text-red-400 text-lg">
                              -${statsForPaperSummary.totalLosses.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                              {language === 'fr' ? 'Cliquez pour détails' : 'Click for details'}
                            </div>
                          </div>
                          {/* PnL Réalisé */}
                          <div 
                            className={`p-3 rounded-lg border cursor-pointer transition-colors ${statsForPaperSummary.totalPnl >= 0 ? 'bg-primary/10 border-primary/20 hover:bg-primary/20' : 'bg-destructive/10 border-destructive/20 hover:bg-destructive/20'}`}
                            onClick={() => setPnlHistoryDialog('total')}
                            data-testid="paper-total-pnl-card"
                          >
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {language === 'fr' ? 'PnL Réalisé' : 'Realized PnL'}
                            </div>
                            <div className={`font-mono font-bold text-lg ${statsForPaperSummary.totalPnl >= 0 ? 'text-primary' : 'text-destructive'}`}>
                              {statsForPaperSummary.totalPnl >= 0 ? '+' : ''}{statsForPaperSummary.totalPnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                              {language === 'fr' ? 'Gains - Pertes' : 'Gains - Losses'}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // TOUS les trades (all) - affiche les 4 cartes
                    return (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Gains Total (clôturés) */}
                        <div 
                          className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 cursor-pointer hover:bg-green-500/20 transition-colors"
                          onClick={() => setPnlHistoryDialog('gains')}
                          data-testid="paper-gains-card"
                        >
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            {language === 'fr' ? 'Gains Clôturés' : 'Closed Gains'}
                          </div>
                          <div className="font-mono font-bold text-green-400 text-lg">
                            +${totals.closedGains.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </div>
                        </div>
                        {/* Pertes Total (clôturées) */}
                        <div 
                          className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 cursor-pointer hover:bg-red-500/20 transition-colors"
                          onClick={() => setPnlHistoryDialog('losses')}
                          data-testid="paper-losses-card"
                        >
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <TrendingDown className="w-3 h-3" />
                            {language === 'fr' ? 'Pertes Clôturées' : 'Closed Losses'}
                          </div>
                          <div className="font-mono font-bold text-red-400 text-lg">
                            -${totals.closedLosses.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </div>
                        </div>
                        {/* Gains Partiels */}
                        <div 
                          className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 cursor-pointer hover:bg-emerald-500/20 transition-colors"
                          onClick={() => setPnlHistoryDialog('partial')}
                          data-testid="paper-partial-gains-card"
                        >
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {language === 'fr' ? 'TP Partiels' : 'Partial TP'}
                          </div>
                          <div className="font-mono font-bold text-emerald-400 text-lg">
                            +${totals.partialTpGains.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </div>
                        </div>
                        {/* PnL Non Réalisé */}
                        <div 
                          className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20 cursor-pointer hover:bg-purple-500/20 transition-colors"
                          onClick={() => setPnlHistoryDialog('unrealized')}
                          data-testid="paper-unrealized-card"
                        >
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Activity className="w-3 h-3" />
                            {language === 'fr' ? 'PnL Latent' : 'Unrealized'}
                          </div>
                          <div className={`font-mono font-bold text-lg ${totals.unrealizedPnl >= 0 ? 'text-purple-400' : 'text-orange-400'}`}>
                            {totals.unrealizedPnl >= 0 ? '+' : ''}{totals.unrealizedPnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            );
            })()}

            {/* Paper Trades List - Compact/Expandable Design */}
            <Card className="bg-card border-purple-500/20" data-testid="paper-trades-list">
              <CardContent className="p-4">
                {filteredPaperTrades.length === 0 ? (
                  <div className="text-center py-16">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">
                      {language === 'fr' ? 'Aucun trade paper dans cette catégorie' : 'No paper trades in this category'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Sort toggle for closed paper trades — chronological order */}
                    {subFilter === 'closed' && filteredPaperTrades.length > 1 && (
                      <div className="flex items-center justify-end mb-2">
                        <button
                          type="button"
                          onClick={() => setClosedSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                          data-testid="closed-paper-trades-sort-toggle"
                          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md border border-white/10 hover:border-white/20"
                          title={closedSortOrder === 'desc'
                            ? (language === 'fr' ? 'Actuellement : plus récents en premier. Cliquer pour inverser.' : 'Currently: most recent first. Click to reverse.')
                            : (language === 'fr' ? 'Actuellement : plus anciens en premier. Cliquer pour inverser.' : 'Currently: oldest first. Click to reverse.')}
                        >
                          {closedSortOrder === 'desc' ? (
                            <>
                              <ArrowDownUp className="w-3 h-3" />
                              {language === 'fr' ? 'Plus récents d\'abord' : 'Newest first'}
                            </>
                          ) : (
                            <>
                              <ArrowDownUp className="w-3 h-3 rotate-180" />
                              {language === 'fr' ? 'Plus anciens d\'abord' : 'Oldest first'}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    {/* Apply pagination only for closed trades */}
                    {(subFilter === 'closed' ? filteredPaperTrades.slice(0, visiblePaperClosedCount) : filteredPaperTrades).map((trade) => {
                      const isExpanded = expandedTrades[trade.id];
                      const currentPrice = getPriceForSymbol(currentPrices, trade.symbol);
                      const entry = trade.entry_price;
                      const sl = trade.stop_loss;
                      const tp1 = trade.take_profit_1;
                      const tp2 = trade.take_profit_2;
                      const tp100 = trade.take_profit_100;
                      const leverage = trade.leverage || 1;
                      const posSize = trade.position_size || 0;
                      const isLong = trade.direction === 'LONG';
                      const isClosed = trade.status === 'WIN' || trade.status === 'LOSS';
                      const isPending = trade.status === 'pending';
                      const isWin = trade.status === 'WIN';
                      
                      // Determine entry type for display
                      let tradeEntryType = trade.auto_test_entry_type || trade.entry_type;
                      if (!tradeEntryType) {
                        if (trade.created_at_price !== null && trade.created_at_price !== undefined) {
                          tradeEntryType = trade.entry_price === trade.created_at_price ? 'market' : 'optimal';
                        } else {
                          tradeEntryType = trade.original_status === 'pending' ? 'optimal' : 'market';
                        }
                      }
                      
                      // For closed trades, use exit_price to determine TP hits
                      const priceToCheck = isClosed ? trade.exit_price : currentPrice;
                      
                      // Check which TPs are hit (never for pending trades)
                      // For WIN trades, ALL TPs are considered hit (since WIN = TP100 reached = all TPs reached)
                      // IMPORTANT: Use warnings_sent from backend (persisted) OR current price check
                      const warnings = trade.warnings_sent || {};
                      
                      // TP hit = either saved in DB (warnings.tp1_hit) OR current price is above TP
                      const tp1HitFromWarnings = warnings.tp1_hit || false;
                      const tp2HitFromWarnings = warnings.tp2_hit || false;
                      const tp100HitFromWarnings = warnings.tp100_hit || false;
                      
                      const tp1HitFromPrice = !isPending && tp1 && priceToCheck && (isLong ? priceToCheck >= tp1 : priceToCheck <= tp1);
                      const tp2HitFromPrice = !isPending && tp2 && priceToCheck && (isLong ? priceToCheck >= tp2 : priceToCheck <= tp2);
                      const tp100HitFromPrice = !isPending && tp100 && priceToCheck && (isLong ? priceToCheck >= tp100 : priceToCheck <= tp100);
                      
                      // Final TP hit status: only from warnings_sent or current price
                      // (WIN status alone must not force ✓ on unrelated TPs)
                      const tp1Hit = tp1HitFromWarnings || (!isClosed && tp1HitFromPrice);
                      const tp2Hit = tp2HitFromWarnings || (!isClosed && tp2HitFromPrice);
                      const tp100Hit = tp100HitFromWarnings || (!isClosed && tp100HitFromPrice);
                      
                      // Calculate unrealized PnL for active trades
                      // IMPORTANT: PnL latent = gain non sécurisé sur la portion restante
                      // Le PnL latent doit être plafonné par les TPs non encore touchés
                      let unrealizedPnl = 0;
                      let unrealizedPnlPct = 0;
                      
                      // For partial gains calculation, use the same TP hit status
                      let tp1ShouldBeHit = tp1Hit;
                      let tp2ShouldBeHit = tp2Hit;
                      
                      // Calculate partial gains (gains sécurisés des TPs touchés)
                      let partialGainsOnTrade = 0;
                      if (tp1ShouldBeHit && tp1 && entry > 0) {
                        const tp1DistPctCalc = Math.abs(tp1 - entry) / entry * 100;
                        partialGainsOnTrade += (tp1DistPctCalc / 100) * posSize * leverage * 0.25;
                      }
                      if (tp2ShouldBeHit && tp2 && entry > 0) {
                        const tp2DistPctCalc = Math.abs(tp2 - entry) / entry * 100;
                        partialGainsOnTrade += (tp2DistPctCalc / 100) * posSize * leverage * 0.25;
                      }
                      
                      if (trade.status === 'active' && currentPrice && entry) {
                        // Calculer le gain % au prix actuel
                        if (isLong) {
                          unrealizedPnlPct = ((currentPrice - entry) / entry) * 100 * leverage;
                        } else {
                          unrealizedPnlPct = ((entry - currentPrice) / entry) * 100 * leverage;
                        }
                        
                        // CORRECTION: Le PnL latent doit être calculé portion par portion
                        // et chaque portion est plafonnée par son TP correspondant
                        
                        // Portion TP1 (25%) - si pas touché, PnL plafonné à TP1
                        let pnlTp1Portion = 0;
                        if (!tp1ShouldBeHit) {
                          const tp1MaxPct = tp1 && entry > 0 ? Math.abs(tp1 - entry) / entry * 100 * leverage : 0;
                          const cappedPct = Math.min(unrealizedPnlPct, tp1MaxPct);
                          pnlTp1Portion = (cappedPct / 100) * posSize * 0.25;
                        }
                        
                        // Portion TP2 (25%) - si pas touché, PnL plafonné à TP2
                        let pnlTp2Portion = 0;
                        if (!tp2ShouldBeHit) {
                          const tp2MaxPct = tp2 && entry > 0 ? Math.abs(tp2 - entry) / entry * 100 * leverage : 0;
                          const cappedPct = Math.min(unrealizedPnlPct, tp2MaxPct);
                          pnlTp2Portion = (cappedPct / 100) * posSize * 0.25;
                        }
                        
                        // Portion TP100 (50%) - PnL complet (pas de plafond)
                        const pnlTp100Portion = (unrealizedPnlPct / 100) * posSize * 0.50;
                        
                        // PnL latent = somme des portions non sécurisées
                        unrealizedPnl = pnlTp1Portion + pnlTp2Portion + pnlTp100Portion;
                      }
                      
                      // Calculate total gain for WIN trades (with partial exit strategy)
                      // For WIN = all TPs hit = 25% + 25% + 50% = 100% of position
                      // Note: posSize here is trade.position_size which is actually the MARGIN
                      const marge = posSize; // position_size IS the margin
                      const positionValue = marge * leverage;
                      const slDistPct = entry > 0 ? Math.abs(sl - entry) / entry * 100 : 0;
                      const tp1DistPct = tp1 && entry > 0 ? Math.abs(tp1 - entry) / entry * 100 : 0;
                      const tp2DistPct = tp2 && entry > 0 ? Math.abs(tp2 - entry) / entry * 100 : 0;
                      const tp100DistPct = tp100 && entry > 0 ? Math.abs(tp100 - entry) / entry * 100 : 0;
                      const gainTp1 = (tp1DistPct / 100) * marge * leverage * 0.25;
                      const gainTp2 = (tp2DistPct / 100) * marge * leverage * 0.25;
                      const gainTp100 = (tp100DistPct / 100) * marge * leverage * 0.50;
                      const gainTotal = gainTp1 + gainTp2 + gainTp100;
                      const gainTotalPct = marge > 0 ? (gainTotal / marge) * 100 : 0;
                      
                      // For display: ALWAYS trust the backend's authoritative `trade.pnl` (which sums
                      // secured legs + market remainder). Fallback to theoretical gainTotal only if
                      // the backend never wrote a PnL (very old data).
                      const displayPnl = (trade.pnl !== null && trade.pnl !== undefined) ? trade.pnl : (isWin ? gainTotal : 0);
                      const displayPnlPct = (trade.pnl_percentage !== null && trade.pnl_percentage !== undefined) ? trade.pnl_percentage : (isWin ? gainTotalPct : 0);
                      const isHighlighted = trade.id === highlightedTradeId;
                      
                      return (
                        <div
                          key={trade.id}
                          ref={isHighlighted ? highlightedTradeRef : null}
                          className={`rounded-xl transition-all overflow-hidden ${
                            isHighlighted 
                              ? 'bg-primary/20 ring-2 ring-primary animate-pulse' 
                              : 'bg-purple-500/5 border border-purple-500/20 hover:bg-purple-500/10'
                          }`}
                          data-testid={`paper-trade-${trade.id}`}
                        >
                          {/* Compact Header - Always Visible */}
                          <div 
                            className="p-4 cursor-pointer"
                            onClick={() => toggleExpanded(trade.id)}
                          >
                            <div className="flex items-center justify-between gap-4">
                              {/* Left: Direction + Symbol + Status */}
                              <div className="flex items-center gap-3">
                                {/* Expand/Collapse Button */}
                                <button className="p-1 rounded hover:bg-white/10 transition-colors">
                                  {isExpanded ? (
                                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                  )}
                                </button>
                                
                                {/* Direction Icon */}
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  trade.direction === 'LONG' 
                                    ? 'bg-primary/20' 
                                    : 'bg-destructive/20'
                                }`}>
                                  {trade.direction === 'LONG' ? (
                                    <TrendingUp className="w-5 h-5 text-primary" />
                                  ) : (
                                    <TrendingDown className="w-5 h-5 text-destructive" />
                                  )}
                                </div>

                                {/* Symbol + Badges */}
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-lg">{getDisplayName(trade.symbol, language)}</span>
                                    <span className="text-xs text-muted-foreground font-mono">({trade.symbol})</span>
                                    <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs">
                                      PAPER
                                    </span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                                      trade.direction === 'LONG' 
                                        ? 'bg-primary/20 text-primary' 
                                        : 'bg-destructive/20 text-destructive'
                                    }`}>
                                      {trade.direction}
                                    </span>
                                    {trade.trade_type && (
                                      <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                                        trade.trade_type === 'SWING' 
                                          ? 'bg-blue-500/20 text-blue-400' 
                                          : 'bg-orange-500/20 text-orange-400'
                                      }`}>
                                        {trade.trade_type}
                                      </span>
                                    )}
                                    {trade.leverage && trade.leverage > 1 && (
                                      <span className="px-2 py-0.5 rounded text-xs font-mono bg-yellow-500/20 text-yellow-400">
                                        {trade.leverage}x
                                      </span>
                                    )}
                                    {trade.profile && (
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        trade.profile === 'conservative' ? 'bg-blue-500/20 text-blue-400' :
                                        trade.profile === 'balanced' ? 'bg-green-500/20 text-green-400' :
                                        trade.profile === 'dynamic' ? 'bg-orange-500/20 text-orange-400' :
                                        'bg-purple-500/20 text-purple-400'
                                      }`}>
                                        {trade.profile === 'conservative' ? '🛡️ Prudent' :
                                         trade.profile === 'balanced' ? '⚖️ Équilibré' :
                                         trade.profile === 'dynamic' ? '🔥 Dynamique' :
                                         trade.profile}
                                      </span>
                                    )}
                                    {/* Badge Type d'entrée */}
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      tradeEntryType === 'market' 
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                                        : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                    }`}>
                                      {tradeEntryType === 'market' ? '⚡ Marché' : '🎯 Optimal'}
                                    </span>
                                    {/* Badge Source (Auto-Scan vs Manuel) - Only show for actual Auto-Scan trades */}
                                    {trade.auto_scan_created && (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-500/20 text-violet-400 border border-violet-500/30">
                                        🤖 Auto-Scan
                                      </span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded text-xs font-mono ${getStatusColor(trade.status)}`}>
                                      {getStatusLabel(trade.status)}
                                    </span>
                                    {/* Date de clôture pour les trades terminés */}
                                    {(trade.status === 'WIN' || trade.status === 'LOSS') && trade.closed_at && (
                                      <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-secondary/50 text-muted-foreground">
                                        <Calendar className="w-3 h-3" />
                                        {new Date(trade.closed_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {(trade.status === 'WIN' || trade.status === 'LOSS') && trade.closed_at
                                      ? `${language === 'fr' ? 'Ouvert le' : 'Opened'} ${new Date(trade.created_at).toLocaleDateString('fr-FR')}`
                                      : new Date(trade.created_at).toLocaleDateString('fr-FR')
                                    }
                                  </div>
                                </div>
                              </div>

                              {/* Center: Current Price (for active/pending) */}
                              {(trade.status === 'active' || trade.status === 'pending') && (
                                <div className="hidden md:flex items-center gap-4">
                                  {currentPrice ? (
                                    <div className="text-center">
                                      <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Prix actuel' : 'Current'}</div>
                                      <div className="font-mono font-bold text-lg flex items-center gap-1">
                                        <Activity className="w-4 h-4 text-purple-400" />
                                        ${formatPrice(currentPrice)}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Prix actuel' : 'Current'}</div>
                                      <div className="font-mono text-muted-foreground">—</div>
                                    </div>
                                  )}
                                  
                                  {/* Unrealized PnL for active trades */}
                                  {trade.status === 'active' && currentPrice && (
                                    <div className="text-center">
                                      <div className="text-xs text-muted-foreground">{language === 'fr' ? 'PnL Latent' : 'Unrealized'}</div>
                                      <div className={`font-mono font-bold ${unrealizedPnl >= 0 ? 'text-purple-400' : 'text-orange-400'}`}>
                                        {unrealizedPnl >= 0 ? '+' : ''}{unrealizedPnl.toFixed(2)}$
                                        <span className="text-xs ml-1">({unrealizedPnlPct >= 0 ? '+' : ''}{unrealizedPnlPct.toFixed(1)}%)</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Partial Gains for active trades with TP hits */}
                                  {trade.status === 'active' && partialGainsOnTrade > 0 && (
                                    <div className="text-center">
                                      <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Gains Sécurisés' : 'Secured'}</div>
                                      <div className="font-mono font-bold text-green-400">
                                        +{partialGainsOnTrade.toFixed(2)}$
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Right: PnL for closed trades OR Quick TPs Status */}
                              <div className="flex items-center gap-3">
                                {/* TP Status Indicators */}
                                {(trade.status === 'active') && (
                                  <div className="hidden sm:flex items-center gap-1">
                                    {tp1 && (
                                      <div className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                                        tp1Hit 
                                          ? 'bg-green-600 dark:bg-green-500 text-white ring-2 ring-green-600 dark:ring-green-400 ring-offset-2 ring-offset-background shadow-md' 
                                          : 'bg-muted/30 text-muted-foreground'
                                      }`}>
                                        TP1 {tp1Hit && '✓'}
                                      </div>
                                    )}
                                    {tp2 && (
                                      <div className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                                        tp2Hit 
                                          ? 'bg-green-600 dark:bg-green-500 text-white ring-2 ring-green-600 dark:ring-green-400 ring-offset-2 ring-offset-background shadow-md' 
                                          : 'bg-muted/30 text-muted-foreground'
                                      }`}>
                                        TP2 {tp2Hit && '✓'}
                                      </div>
                                    )}
                                    {tp100 && (
                                      <div className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                                        tp100Hit 
                                          ? 'bg-emerald-600 dark:bg-emerald-500 text-white ring-2 ring-emerald-600 dark:ring-emerald-400 ring-offset-2 ring-offset-background shadow-md animate-pulse' 
                                          : 'bg-muted/30 text-muted-foreground'
                                      }`}>
                                        TP100 {tp100Hit && '✓'}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* PnL for closed trades */}
                                {(trade.status === 'WIN' || trade.status === 'LOSS') && (displayPnl !== undefined || trade.pnl !== undefined) && (
                                  <div className="text-right">
                                    <div className={`font-mono font-bold text-lg ${(displayPnl || 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                                      {(displayPnl || 0) >= 0 ? '+' : ''}{(displayPnl || 0).toFixed(2)}$
                                    </div>
                                    <div className={`text-xs ${(displayPnlPct || 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                                      {(displayPnlPct || 0) >= 0 ? '+' : ''}{(displayPnlPct || 0).toFixed(2)}%
                                    </div>
                                  </div>
                                )}
                                
                                {/* Actions Menu */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="icon" data-testid={`paper-trade-menu-${trade.id}`}>
                                      <MoreVertical className="w-5 h-5" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-card border-white/10">
                                    {trade.status === 'pending' && (
                                      <DropdownMenuItem onClick={() => handleCloseTrade(trade, 'active')}>
                                        <Play className="w-4 h-4 mr-2 text-yellow-400" />
                                        {language === 'fr' ? 'Activer' : 'Activate'}
                                      </DropdownMenuItem>
                                    )}
                                    {(trade.status === 'pending' || trade.status === 'active') && (
                                      <DropdownMenuItem 
                                        onClick={() => handleCloseTrade(trade, 'CLOSE')}
                                        className="text-primary focus:text-primary"
                                        data-testid={`paper-close-trade-market-${trade.id}`}
                                      >
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        {language === 'fr' ? 'Clôturer au marché' : 'Close at market'}
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem 
                                      onClick={() => deleteTrade(trade.id)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      {language === 'fr' ? 'Supprimer' : 'Delete'}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                            
                            {/* Mobile: Current Price + PnL */}
                            {(trade.status === 'active' || trade.status === 'pending') && currentPrice && (
                              <div className="md:hidden mt-3 pt-3 border-t border-purple-500/10 flex items-center justify-between">
                                <div>
                                  <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Prix actuel' : 'Current'}: </span>
                                  <span className="font-mono font-bold">${formatPrice(currentPrice)}</span>
                                </div>
                                {trade.status === 'active' && (
                                  <div className={`font-mono font-bold ${unrealizedPnl >= 0 ? 'text-primary' : 'text-destructive'}`}>
                                    {unrealizedPnl >= 0 ? '+' : ''}{unrealizedPnl.toFixed(2)}$ ({unrealizedPnlPct >= 0 ? '+' : ''}{unrealizedPnlPct.toFixed(1)}%)
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t border-purple-500/10">
                              {(() => {
                                const entry = trade.entry_price || 0;
                                const sl = trade.stop_loss || 0;
                                const tp1 = trade.take_profit_1;
                                const tp2 = trade.take_profit_2;
                                const tp100 = trade.take_profit_100;
                                const leverage = trade.leverage || 1;
                                const marge = trade.position_size || 0;
                                const positionValue = marge * leverage;
                                const posSize = entry > 0 ? positionValue / entry : 0;
                                const isLong = trade.direction === 'LONG';
                                const currentPrice = getPriceForSymbol(currentPrices, trade.symbol);
                                const isClosed = trade.status === 'WIN' || trade.status === 'LOSS';
                                const isPending = trade.status === 'pending';
                                
                                // For closed trades, use exit_price to determine TP hits
                                const priceToCheck = isClosed ? trade.exit_price : currentPrice;

                                // TP hit indicators for the price-levels bar:
                                // - For CLOSED trades: trust `warnings_sent.tpX_hit` (backend authoritative) ONLY.
                                //   Don't blanket-mark all TPs as hit just because status=WIN — a WIN can happen
                                //   with only 1 TP secured (rest at market).
                                // - For ACTIVE trades: also check if the current price is currently at/past the TP.
                                const warnings = trade.warnings_sent || {};
                                const tp1HitFromWarnings = warnings.tp1_hit || false;
                                const tp2HitFromWarnings = warnings.tp2_hit || false;
                                const tp100HitFromWarnings = warnings.tp100_hit || false;
                                const tp1HitFromPrice = !isPending && !isClosed && tp1 && priceToCheck && (isLong ? priceToCheck >= tp1 : priceToCheck <= tp1);
                                const tp2HitFromPrice = !isPending && !isClosed && tp2 && priceToCheck && (isLong ? priceToCheck >= tp2 : priceToCheck <= tp2);
                                const tp100HitFromPrice = !isPending && !isClosed && tp100 && priceToCheck && (isLong ? priceToCheck >= tp100 : priceToCheck <= tp100);
                                const tp1Hit = tp1HitFromWarnings || tp1HitFromPrice;
                                const tp2Hit = tp2HitFromWarnings || tp2HitFromPrice;
                                const tp100Hit = tp100HitFromWarnings || tp100HitFromPrice;
                                
                                // Calculate distances (percentage without leverage)
                                const slDistPct = entry > 0 ? Math.abs(sl - entry) / entry * 100 : 0;
                                const tp1DistPct = tp1 && entry > 0 ? Math.abs(tp1 - entry) / entry * 100 : 0;
                                const tp2DistPct = tp2 && entry > 0 ? Math.abs(tp2 - entry) / entry * 100 : 0;
                                const tp100DistPct = tp100 && entry > 0 ? Math.abs(tp100 - entry) / entry * 100 : 0;
                                
                                // Calculate PnL at levels WITH PARTIAL EXIT STRATEGY (25% + 25% + 50%)
                                const perteSL = (slDistPct / 100) * marge * leverage;
                                const gainTp1 = (tp1DistPct / 100) * marge * leverage * 0.25;  // 25%
                                const gainTp2 = (tp2DistPct / 100) * marge * leverage * 0.25;  // 25%
                                const gainTp100 = (tp100DistPct / 100) * marge * leverage * 0.50; // 50%
                                const gainTotal = gainTp1 + gainTp2 + gainTp100;  // Somme des 3 TPs
                                
                                // R:R basé sur la DISTANCE pure (TP100-Entry) / (Entry-SL)
                                // On utilise le risk_reward_ratio du backend s'il existe, sinon on calcule
                                const rr = trade.risk_reward_ratio || (tp100DistPct > 0 && slDistPct > 0 ? tp100DistPct / slDistPct : 0);
                                
                                // Liquidation price
                                let liqPrice = 0;
                                if (leverage > 1) {
                                  liqPrice = isLong 
                                    ? entry * (1 - 1/leverage)
                                    : entry * (1 + 1/leverage);
                                }
                                const slAfterLiq = leverage > 1 && ((isLong && sl < liqPrice) || (!isLong && sl > liqPrice));
                                
                                return (
                                  <div className="space-y-4 mt-4">
                                    {/* Grid Stats */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                      <div className="p-3 rounded-lg bg-purple-500/10">
                                        <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Marge' : 'Margin'}</div>
                                        <div className="font-mono font-bold text-yellow-400">${marge.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                                      </div>
                                      <div className="p-3 rounded-lg bg-purple-500/10">
                                        <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Position' : 'Position'}</div>
                                        <div className="font-mono font-bold">${positionValue.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                                      </div>
                                      <div className="p-3 rounded-lg bg-purple-500/10">
                                        <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Quantite' : 'Qty'}</div>
                                        <div className="font-mono font-bold">{posSize.toFixed(6)}</div>
                                      </div>
                                      <div className="p-3 rounded-lg bg-purple-500/10">
                                        <div className="text-xs text-muted-foreground">R:R</div>
                                        <div className="font-mono font-bold text-blue-400">{rr.toFixed(2)}:1</div>
                                      </div>
                                      <div className={`p-3 rounded-lg border ${slAfterLiq ? 'bg-red-900/30 border-red-500/50' : 'bg-destructive/10 border-destructive/20'}`}>
                                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                                          {slAfterLiq && <AlertTriangle className="w-3 h-3 text-red-400" />}
                                          {language === 'fr' ? 'Perte SL' : 'SL Loss'}
                                        </div>
                                        {slAfterLiq ? (
                                          <div className="font-mono font-bold text-red-400 flex items-center gap-1">
                                            <AlertTriangle className="w-4 h-4" />
                                            {language === 'fr' ? 'Liquidé' : 'Liquidated'}
                                          </div>
                                        ) : (
                                          <div className="font-mono font-bold text-destructive">-${perteSL.toFixed(2)}</div>
                                        )}
                                      </div>
                                      <div className={`p-3 rounded-lg bg-gradient-to-r ${isClosed ? ((trade.pnl ?? 0) >= 0 ? 'from-green-500/20 to-emerald-500/20 border-green-500/30' : 'from-red-500/20 to-rose-500/20 border-red-500/30') : 'from-green-500/20 to-emerald-500/20 border-green-500/30'} border`}>
                                        <div className="text-xs text-muted-foreground">
                                          {isClosed
                                            ? (language === 'fr' ? 'Gain / Perte réel' : 'Actual P&L')
                                            : (language === 'fr' ? 'Gain Total max' : 'Max Total Profit')}
                                        </div>
                                        <div className={`font-mono font-bold ${isClosed ? ((trade.pnl ?? 0) >= 0 ? 'text-green-400' : 'text-red-400') : 'text-green-400'}`}>
                                          {isClosed
                                            ? `${(trade.pnl ?? 0) >= 0 ? '+' : ''}$${(trade.pnl ?? 0).toFixed(2)}`
                                            : `+$${gainTotal.toFixed(2)}`}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Avertissement frais */}
                                    <p className="text-xs text-muted-foreground/60 italic">
                                      {language === 'fr' 
                                        ? '* Gains approximatifs, frais d\'exchange non inclus' 
                                        : '* Approximate profits, exchange fees not included'}
                                    </p>
                                    
                                    {/* Price Levels with TP hit indicators */}
                                    <div className="p-3 rounded-lg bg-purple-500/5">
                                      <div className="text-xs text-muted-foreground mb-2">{language === 'fr' ? 'Niveaux de prix' : 'Price Levels'}</div>
                                      <div className="flex items-center justify-between text-sm flex-wrap gap-2">
                                        {/* Liquidation */}
                                        {liqPrice > 0 && (
                                          <div className={`p-2 rounded ${slAfterLiq ? 'bg-red-900/50 border-2 border-red-500' : 'bg-red-500/10'}`}>
                                            <div className="text-xs text-red-400">Liq.</div>
                                            <div className="font-mono font-bold text-red-400">${formatPrice(liqPrice)}</div>
                                            <div className="text-xs font-mono text-red-400">-${marge.toFixed(2)}</div>
                                          </div>
                                        )}
                                        
                                        {/* SL */}
                                        <div className="p-2 rounded bg-destructive/10">
                                          <div className="text-xs text-destructive">SL</div>
                                          <div className="font-mono font-bold text-destructive">${formatPrice(sl)}</div>
                                        </div>
                                        
                                        {/* Entry */}
                                        <div className="p-2 rounded bg-white/10">
                                          <div className="text-xs text-muted-foreground">Entry</div>
                                          <div className="font-mono font-bold">${formatPrice(entry)}</div>
                                        </div>
                                        
                                        {/* TP1 */}
                                        {tp1 && (
                                          <div className={`p-2 rounded transition-all ${
                                            tp1Hit 
                                              ? 'bg-green-600 dark:bg-green-500 text-white ring-2 ring-green-500 dark:ring-green-400 ring-offset-2 ring-offset-background shadow-lg' 
                                              : 'bg-primary/10'
                                          }`}>
                                            <div className={`text-xs font-medium ${tp1Hit ? 'text-white' : 'text-primary/70'}`}>TP1 (25%) {tp1Hit && '✓'}</div>
                                            <div className={`font-mono font-bold ${tp1Hit ? 'text-white' : 'text-primary/70'}`}>${formatPrice(tp1)}</div>
                                            <div className={`text-xs ${tp1Hit ? 'text-green-100' : 'text-muted-foreground'}`}>+${gainTp1.toFixed(2)}</div>
                                          </div>
                                        )}
                                        
                                        {/* TP2 */}
                                        {tp2 && (
                                          <div className={`p-2 rounded transition-all ${
                                            tp2Hit 
                                              ? 'bg-green-600 dark:bg-green-500 text-white ring-2 ring-green-500 dark:ring-green-400 ring-offset-2 ring-offset-background shadow-lg' 
                                              : 'bg-primary/10'
                                          }`}>
                                            <div className={`text-xs font-medium ${tp2Hit ? 'text-white' : 'text-primary/80'}`}>TP2 (50%) {tp2Hit && '✓'}</div>
                                            <div className={`font-mono font-bold ${tp2Hit ? 'text-white' : 'text-primary/80'}`}>${formatPrice(tp2)}</div>
                                            <div className={`text-xs ${tp2Hit ? 'text-green-100' : 'text-muted-foreground'}`}>+${gainTp2.toFixed(2)}</div>
                                          </div>
                                        )}
                                        
                                        {/* TP100 */}
                                        {tp100 && (
                                          <div className={`p-2 rounded border transition-all ${
                                            tp100Hit 
                                              ? 'bg-emerald-600 dark:bg-emerald-500 text-white ring-2 ring-emerald-500 dark:ring-emerald-400 ring-offset-2 ring-offset-background shadow-lg animate-pulse' 
                                              : 'bg-primary/20 border-primary/30'
                                          }`}>
                                            <div className={`text-xs font-medium ${tp100Hit ? 'text-white' : 'text-primary'}`}>TP100% (50%) {tp100Hit && '✓'}</div>
                                            <div className={`font-mono font-bold ${tp100Hit ? 'text-white' : 'text-primary'}`}>${formatPrice(tp100)}</div>
                                            <div className={`text-xs ${tp100Hit ? 'text-emerald-100' : 'text-primary/70'}`}>+${gainTp100.toFixed(2)}</div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Liquidation Warning */}
                                    {slAfterLiq && (
                                      <div className="p-3 rounded-lg bg-red-900/30 border border-red-500/50 flex items-start gap-2">
                                        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                          <div className="font-medium text-red-400">
                                            {language === 'fr' ? 'Attention : Liquidation avant SL' : 'Warning: Liquidation before SL'}
                                          </div>
                                          <div className="text-sm text-red-300/80">
                                            {language === 'fr' 
                                              ? `Avec un levier de ${leverage}x, liquidation a $${liqPrice.toFixed(2)} avant le SL.`
                                              : `With ${leverage}x leverage, liquidation at $${liqPrice.toFixed(2)} before SL.`
                                            }
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Notes */}
                                    {trade.notes && (
                                      <div className="p-3 rounded-lg bg-purple-500/5">
                                        <div className="text-xs text-muted-foreground mb-1">Notes</div>
                                        <p className="text-sm">{trade.notes}</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Load More Button for Closed Paper Trades */}
                {subFilter === 'closed' && filteredPaperTrades.length > visiblePaperClosedCount && (
                  <div className="mt-4 text-center">
                    <Button
                      variant="outline"
                      onClick={() => setVisiblePaperClosedCount(prev => prev + TRADES_PER_PAGE)}
                      className="w-full max-w-xs border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                      data-testid="load-more-paper-trades"
                    >
                      <ChevronDown className="w-4 h-4 mr-2" />
                      {language === 'fr' 
                        ? `Voir plus (${Math.min(TRADES_PER_PAGE, filteredPaperTrades.length - visiblePaperClosedCount)} sur ${filteredPaperTrades.length - visiblePaperClosedCount} restants)`
                        : `Load more (${Math.min(TRADES_PER_PAGE, filteredPaperTrades.length - visiblePaperClosedCount)} of ${filteredPaperTrades.length - visiblePaperClosedCount} remaining)`
                      }
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      {language === 'fr' 
                        ? `Affichage de ${visiblePaperClosedCount} sur ${filteredPaperTrades.length} trades`
                        : `Showing ${visiblePaperClosedCount} of ${filteredPaperTrades.length} trades`
                      }
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Close Trade Modal - Global pour Real et Paper */}
      {closingTrade && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-md bg-card border-border my-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                {language === 'fr' ? 'Clôturer le Trade' : 'Close Trade'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-secondary/30">
                <div className="font-mono font-bold">{closingTrade.symbol}</div>
                <div className="text-sm text-muted-foreground">
                  {closingTrade.direction} | {language === 'fr' ? 'Entrée' : 'Entry'}: ${closingTrade.entry_price}
                  {closingTrade.is_paper && <span className="ml-2 text-purple-400">(PAPER)</span>}
                </div>
                {/* Prix marché en direct */}
                {getPriceForSymbol(currentPrices, closingTrade.symbol) && (
                  <div className="mt-2 flex items-center justify-between text-sm border-t border-white/10 pt-2">
                    <span className="text-muted-foreground">
                      {language === 'fr' ? 'Prix actuel du marché' : 'Current market price'}
                    </span>
                    <span className="font-mono font-bold text-primary" data-testid="close-modal-market-price">
                      ${getPriceForSymbol(currentPrices, closingTrade.symbol)?.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{language === 'fr' ? 'Prix de sortie' : 'Exit Price'}</Label>
                  {/* Bouton "Marché" pour resetter au prix live */}
                  {getPriceForSymbol(currentPrices, closingTrade.symbol) && (
                    <button
                      type="button"
                      onClick={() => setExitPrice(getPriceForSymbol(currentPrices, closingTrade.symbol).toString())}
                      className="text-xs text-primary hover:underline"
                      data-testid="reset-to-market-btn"
                    >
                      {language === 'fr' ? '↻ Utiliser prix marché' : '↻ Use market price'}
                    </button>
                  )}
                </div>
                <Input
                  type="number"
                  step="0.01"
                  value={exitPrice}
                  onChange={(e) => setExitPrice(e.target.value)}
                  placeholder="0.00"
                  className="font-mono"
                  data-testid="exit-price-input"
                />
              </div>
              
              {/* TPs déjà atteints - toujours affichés (calcul PnL automatique) */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Take Profits touchés' : 'Take Profits hit'}</Label>
                <div className="text-xs text-muted-foreground -mt-1 mb-2">
                  {language === 'fr'
                    ? 'Les TPs déjà touchés sont sécurisés. Le PnL sera calculé automatiquement : gains sécurisés + reste au prix de sortie.'
                    : 'Hit TPs are secured. PnL is calculated automatically: secured gains + remainder at exit price.'}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'tp1', label: 'TP1 (25%)', hit: closingTrade.warnings_sent?.tp1_hit, price: closingTrade.take_profit_1 },
                    { key: 'tp2', label: 'TP2 (25%)', hit: closingTrade.warnings_sent?.tp2_hit, price: closingTrade.take_profit_2 },
                    { key: 'tp100', label: 'TP100 (50%)', hit: closingTrade.warnings_sent?.tp100_hit, price: closingTrade.take_profit_100 || closingTrade.take_profit_3 },
                  ].map((tp) => (
                    <div
                      key={tp.key}
                      className={`rounded-md border px-3 py-2 text-center text-xs ${
                        tp.hit
                          ? 'border-primary/60 bg-primary/10 text-primary'
                          : 'border-muted-foreground/20 bg-muted/20 text-muted-foreground'
                      }`}
                      data-testid={`tp-status-${tp.key}`}
                    >
                      <div className="font-semibold flex items-center justify-center gap-1">
                        {tp.hit && <span>✓</span>}
                        {tp.label}
                      </div>
                      {tp.price && <div className="font-mono opacity-80">${tp.price.toFixed(2)}</div>}
                      <div className="text-[10px] mt-1 uppercase tracking-widest">
                        {tp.hit ? (language === 'fr' ? 'Sécurisé' : 'Secured') : (language === 'fr' ? 'Non atteint' : 'Not hit')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {exitPrice && closingTrade.entry_price && (
                <div className="p-3 rounded-lg bg-secondary/20 space-y-2">
                  <div className="text-sm text-muted-foreground">PNL estimé</div>
                  {(() => {
                    const entry = closingTrade.entry_price;
                    const exit = parseFloat(exitPrice);
                    const marge = closingTrade.position_size || 0;
                    const leverage = closingTrade.leverage || 1;
                    const dirSign = closingTrade.direction === 'LONG' ? 1 : -1;
                    const legPnl = (price, weight) => {
                      if (!price || entry <= 0) return 0;
                      return ((price - entry) / entry) * dirSign * leverage * marge * weight;
                    };
                    const ws = closingTrade.warnings_sent || {};
                    const tp1 = closingTrade.take_profit_1;
                    const tp2 = closingTrade.take_profit_2;
                    const tp100 = closingTrade.take_profit_100 || closingTrade.take_profit_3;
                    let secured = 0, remaining = 1;
                    if (ws.tp1_hit && tp1) { secured += legPnl(tp1, 0.25); remaining -= 0.25; }
                    if (ws.tp2_hit && tp2) { secured += legPnl(tp2, 0.25); remaining -= 0.25; }
                    if (ws.tp100_hit && tp100) { secured += legPnl(tp100, 0.50); remaining -= 0.50; }
                    remaining = Math.max(remaining, 0);
                    const remainingPnl = remaining > 0 ? legPnl(exit, remaining) : 0;
                    let total = secured + remainingPnl;
                    if (marge > 0) total = Math.max(total, -marge);
                    const totalPct = marge > 0 ? (total / marge) * 100 : 0;
                    return (
                      <>
                        {secured !== 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Gains sécurisés</span>
                            <span className="font-mono text-primary">+{secured.toFixed(2)}$</span>
                          </div>
                        )}
                        {remaining > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Reste ({(remaining * 100).toFixed(0)}%) au prix marché</span>
                            <span className={`font-mono ${remainingPnl >= 0 ? 'text-primary' : 'text-destructive'}`}>{remainingPnl >= 0 ? '+' : ''}{remainingPnl.toFixed(2)}$</span>
                          </div>
                        )}
                        <div className="flex justify-between items-baseline border-t border-border/50 pt-2 mt-2">
                          <span className="text-xs uppercase tracking-widest text-muted-foreground">Total</span>
                          <span className={`font-mono font-bold text-lg ${total >= 0 ? 'text-primary' : 'text-destructive'}`}>
                            {total >= 0 ? '+' : ''}{total.toFixed(2)}$
                            <span className="text-xs ml-2 opacity-80">({totalPct >= 0 ? '+' : ''}{totalPct.toFixed(2)}%)</span>
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => { setClosingTrade(null); setExitPrice(''); }}
                  className="flex-1"
                >
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button 
                  onClick={confirmCloseTrade}
                  disabled={!exitPrice}
                  className="flex-1 bg-primary hover:bg-primary/90"
                  data-testid="confirm-close-btn"
                >
                  {language === 'fr' ? 'Clôturer au marché' : 'Close at market'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog d'activation de trade */}
      <ActivateTradeDialog
        language={language}
        trade={activatingTrade}
        activationPrice={activationPrice}
        setActivationPrice={setActivationPrice}
        onConfirm={confirmActivateTrade}
        onCancel={() => { setActivatingTrade(null); setActivationPrice(''); }}
      />

      {/* Modal de confirmation Reset */}
      <ResetPortfolioDialog
        language={language}
        open={showResetConfirm}
        onOpenChange={setShowResetConfirm}
        confirmText={resetConfirmText}
        setConfirmText={setResetConfirmText}
        onConfirm={resetPaperPortfolio}
        isLoading={isResetting}
      />

      {/* PnL History Dialog */}
      <PnLHistoryDialog
        language={language}
        dialogType={pnlHistoryDialog}
        onClose={() => setPnlHistoryDialog(null)}
        trades={activeTab === 'real' ? trades : paperTrades}
        calculateTotals={calculateTotals}
        currentPrices={currentPrices}
        getDisplayName={getDisplayName}
        formatPrice={formatPrice}
      />
    </div>
  );
};

export default History;
